import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-RGHW4PYM.mjs";

// src/components/avatar/index.tsx
import clsx from "clsx";

// src/components/avatar/placeholder-avatar-organization.svg
var placeholder_avatar_organization_default = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none">%0A    <path fill="%23F6F6F8" d="M0 0h40v40H0z" />%0A    <path%0A        d="M9.5 28.25h21M21.5 28.25v-16.5a.75.75 0 00-.75-.75h-9a.75.75 0 00-.75.75v16.5M29 28.25v-10.5a.75.75 0 00-.75-.75H21.5M14 14.75h3M15.5 20.75h3M14 24.5h3M24.5 24.5H26M24.5 20.75H26"%0A        stroke="%23282A30" stroke-linecap="round" stroke-linejoin="round" />%0A</svg>%0A';

// src/components/avatar/placeholder-avatar-user.svg
var placeholder_avatar_user_default = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none">%0A    <rect width="40" height="40" fill="%23F6F6F8" />%0A    <path d="M20 23a6 6 0 100-12 6 6 0 000 12zM10.906 28.25a10.501 10.501 0 0118.188 0" stroke="%23282A30"%0A        stroke-linecap="round" stroke-linejoin="round" />%0A</svg>%0A';

// src/components/avatar/index.tsx
import { jsx } from "react/jsx-runtime";
function Avatar({
  avatar,
  className
}) {
  var _a;
  const fallbackUrl = avatar.kind === "organization" ? placeholder_avatar_organization_default : placeholder_avatar_user_default;
  const src = (_a = avatar.url) != null ? _a : fallbackUrl;
  return /* @__PURE__ */ jsx(
    "img",
    {
      "data-avatar-kind": avatar.kind,
      className: clsx("r8-avatar", className),
      src,
      alt: "",
      role: "presentation",
      onError: (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = fallbackUrl;
      }
    }
  );
}

// src/components/button/index.tsx
import {
  Button as AKButton
} from "@ariakit/react";
import { cva } from "class-variance-authority";
import { forwardRef as forwardRef2 } from "react";

// src/util.ts
import { clsx as clsx2 } from "clsx";
import { forwardRef } from "react";
function cn(...inputs) {
  return clsx2(inputs);
}
var fixedForwardRef = forwardRef;

// src/components/spinner/index.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function Spinner() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: "r8-spinner",
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      children: [
        /* @__PURE__ */ jsx2(
          "circle",
          {
            style: {
              opacity: 0.25
            },
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            style: {
              opacity: 0.75
            },
            fill: "currentColor",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          }
        )
      ]
    }
  );
}

// src/components/button/index.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var buttonCva = cva("r8-btn", {
  variants: {
    variant: {
      filled: "r8-btn--filled",
      outlined: "r8-btn--outlined",
      clear: "r8-btn--clear"
    },
    loading: {
      true: "r8-btn--disabled"
    },
    disabled: {
      true: "r8-btn--disabled"
    },
    intent: {
      primary: "r8-btn--primary",
      success: "r8-btn--success",
      danger: "r8-btn--danger",
      info: "r8-btn--info",
      warning: "r8-btn--warning",
      brand: "r8-btn--brand"
    },
    underlined: {
      true: "r8-btn--link"
    },
    round: {
      true: "r8-btn--round"
    },
    size: {
      xs: "r8-btn--xs",
      sm: "r8-btn--sm",
      md: "r8-btn--md",
      lg: "r8-btn--lg"
    }
  }
});
var Button = forwardRef2(
  (_a, ref) => {
    var _b = _a, {
      variant = "filled",
      intent = "primary",
      size = "md",
      startIcon,
      endIcon,
      round,
      underlined,
      disabled,
      loading,
      children,
      className
    } = _b, rest = __objRest(_b, [
      "variant",
      "intent",
      "size",
      "startIcon",
      "endIcon",
      "round",
      "underlined",
      "disabled",
      "loading",
      "children",
      "className"
    ]);
    const btnClassName = cn(
      buttonCva({
        variant,
        intent,
        size,
        round,
        underlined,
        loading,
        disabled,
        className
      })
    );
    if (loading) {
      startIcon = /* @__PURE__ */ jsx3(Spinner, {});
      endIcon = null;
    }
    return /* @__PURE__ */ jsxs2(
      AKButton,
      __spreadProps(__spreadValues({}, rest), {
        ref,
        disabled: disabled || loading,
        className: btnClassName,
        children: [
          startIcon ? /* @__PURE__ */ jsx3("span", { className: "r8-btn__icon", children: startIcon }) : null,
          children,
          endIcon ? /* @__PURE__ */ jsx3("span", { className: "r8-btn__icon", children: endIcon }) : null
        ]
      })
    );
  }
);
Button.displayName = "Button";
var IconButton = forwardRef2(
  (_a, ref) => {
    var _b = _a, {
      variant = "filled",
      intent = "primary",
      size = "md",
      loading,
      disabled,
      className,
      children
    } = _b, rest = __objRest(_b, [
      "variant",
      "intent",
      "size",
      "loading",
      "disabled",
      "className",
      "children"
    ]);
    const btnClassName = cn(
      buttonCva({
        variant,
        intent,
        size,
        loading,
        disabled,
        className: `r8-btn--icon ${className}`
      })
    );
    return /* @__PURE__ */ jsx3(
      AKButton,
      __spreadProps(__spreadValues({}, rest), {
        disabled,
        className: btnClassName,
        ref,
        children: /* @__PURE__ */ jsx3("span", { className: "r8-btn__icon", children: loading ? /* @__PURE__ */ jsx3(Spinner, {}) : children })
      })
    );
  }
);
IconButton.displayName = "IconButton";

// src/components/banner/index.tsx
import { X } from "@phosphor-icons/react";
import { forwardRef as forwardRef3 } from "react";

// src/variants.ts
import { cva as cva2 } from "class-variance-authority";
var formGroup = cva2("r8-form-group", {
  variants: {
    size: {
      sm: "r8-form-group--sm",
      md: "r8-form-group--md",
      lg: "r8-form-group--lg"
    },
    variant: {
      error: "r8-form-group--error"
    }
  }
});
var tabs = cva2("r8-tabs", {
  variants: {
    variant: {
      bordered: "r8-tabs--bordered",
      segmented: "r8-tabs--segmented",
      pills: "r8-tabs--pills"
    },
    size: {
      sm: "r8-tabs--sm",
      md: "r8-tabs--md"
    }
  },
  defaultVariants: {
    variant: "bordered",
    size: "md"
  }
});
var badge = cva2("r8-badge", {
  variants: {
    variant: {
      default: "r8-badge--default",
      warning: "r8-badge--warning",
      accent: "r8-badge--accent",
      danger: "r8-badge--danger",
      success: "r8-badge--success"
    },
    size: {
      sm: "r8-badge--sm",
      md: "r8-badge--md"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var box = cva2("r8-box", {
  variants: {
    variant: {
      card: "r8-box--card",
      default: "r8-box--default",
      "tinted-frame": "r8-box--tinted-frame",
      well: "r8-box--well"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var selectItem = cva2("r8-select__item", {
  variants: {
    intent: {
      default: "r8-select__item--default",
      danger: "r8-select__item--danger"
    }
  },
  defaultVariants: {
    intent: "default"
  }
});
var dialog = cva2("r8-dialog", {
  variants: {
    variant: {
      default: "r8-dialog--default",
      drawer: "r8-dialog--drawer"
    },
    side: {
      left: "r8-dialog--drawer-left",
      right: "r8-dialog--drawer-right",
      bottom: "r8-dialog--drawer-bottom",
      top: "r8-dialog--drawer-top"
    },
    size: {
      sm: "r8-dialog--sm",
      md: "r8-dialog--md",
      lg: "r8-dialog--lg",
      xl: "r8-dialog--xl"
    }
  },
  defaultVariants: {
    variant: "default",
    side: "right",
    size: "md"
  }
});
var banner = cva2("r8-banner", {
  variants: {
    severity: {
      default: "r8-banner--default",
      info: "r8-banner--info",
      warning: "r8-banner--warning",
      error: "r8-banner--error",
      success: "r8-banner--success"
    }
  },
  defaultVariants: {
    severity: "default"
  }
});

// src/components/banner/index.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Banner = forwardRef3(
  (_a, ref) => {
    var _b = _a, {
      severity = "default",
      icon,
      heading,
      description,
      meta,
      dismissible,
      onDismiss,
      condensed = false,
      raised = false,
      fullWidth = false,
      flushTop = false,
      centered = false
    } = _b, rest = __objRest(_b, [
      "severity",
      "icon",
      "heading",
      "description",
      "meta",
      "dismissible",
      "onDismiss",
      "condensed",
      "raised",
      "fullWidth",
      "flushTop",
      "centered"
    ]);
    const bannerClassName = cn(
      banner({
        severity
      }),
      {
        "r8-banner--condensed": condensed,
        "r8-banner--raised": raised,
        "r8-banner--full-width": fullWidth,
        "r8-banner--flush-top": flushTop,
        "r8-banner--centered": centered
      }
    );
    return /* @__PURE__ */ jsxs3("div", __spreadProps(__spreadValues({ ref, className: bannerClassName }, rest), { children: [
      dismissible && /* @__PURE__ */ jsx4(
        "button",
        {
          type: "button",
          onClick: () => {
            onDismiss == null ? void 0 : onDismiss();
          },
          className: "r8-banner--dismissible",
          children: /* @__PURE__ */ jsx4(X, { weight: "bold" })
        }
      ),
      icon && /* @__PURE__ */ jsx4("div", { className: "r8-banner__icon", children: icon }),
      /* @__PURE__ */ jsxs3("div", { className: "r8-banner__body", children: [
        /* @__PURE__ */ jsxs3("div", { className: "r8-banner__copy", children: [
          heading ? /* @__PURE__ */ jsx4("div", { className: "r8-banner--heading", children: heading }) : null,
          /* @__PURE__ */ jsx4("div", { className: "r8-banner--description", children: description })
        ] }),
        meta ? /* @__PURE__ */ jsx4("div", { className: "r8-banner__meta", children: meta }) : null
      ] })
    ] }));
  }
);

// src/components/checkbox/index.tsx
import {
  Checkbox as AKCheckbox,
  useCheckboxStore,
  useCheckboxContext,
  CheckboxProvider
} from "@ariakit/react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Checkbox = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx5(
    AKCheckbox,
    __spreadProps(__spreadValues({
      className: cn("r8-checkbox", props.className)
    }, props), {
      ref
    })
  );
});

// src/components/label/index.tsx
import { cva as cva3 } from "class-variance-authority";
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var labelCva = cva3("r8-label", {
  variants: {
    variant: {
      mono: "r8-label--mono"
    }
  }
});
var Label = forwardRef4(
  (_a, ref) => {
    var _b = _a, { variant, className } = _b, rest = __objRest(_b, ["variant", "className"]);
    const labelClass = labelCva({ variant, className });
    return /* @__PURE__ */ jsx6("label", __spreadProps(__spreadValues({}, rest), { className: labelClass, ref }));
  }
);
Label.displayName = "Label";

// src/components/tabs/index.tsx
import {
  Tab as AKTab,
  TabList as AKTabList,
  TabPanel as AKTabPanel,
  TabProvider,
  useTabContext,
  useTabStore
} from "@ariakit/react";
import {
  useEffect,
  useRef,
  useState
} from "react";
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
function ResponsiveTabList(props) {
  const _a = props, { children } = _a, rest = __objRest(_a, ["children"]);
  const [overflowing, setOverflowing] = useState(false);
  const tabsContainerRef = useRef(null);
  const context = useTabContext();
  if (!context)
    throw new Error("ResponsiveTabList must be used inside a TabProvider");
  const activeId = context.useState((state) => state.activeId);
  useEffect(() => {
    const tabsContainer = tabsContainerRef.current;
    const checkOverflow = () => {
      if (tabsContainer) {
        if (tabsContainer.scrollWidth > tabsContainer.clientWidth) {
          setOverflowing(true);
        } else {
          setOverflowing(false);
        }
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);
  if (!Array.isArray(children)) return;
  const flattened = children.filter(Boolean).flatMap((child) => {
    var _a2;
    if (Array.isArray((_a2 = child == null ? void 0 : child.props) == null ? void 0 : _a2.children)) {
      return child.props.children;
    }
    return child;
  }).filter(Boolean);
  return /* @__PURE__ */ jsxs4("div", { className: "r8-tabs--responsive-grid", children: [
    /* @__PURE__ */ jsx7(
      "div",
      {
        "aria-hidden": overflowing,
        className: "r8-tabs--responsive-tab-list-wrap",
        style: {
          opacity: overflowing ? 0 : 1,
          pointerEvents: overflowing ? "none" : "auto"
        },
        ref: tabsContainerRef,
        children: /* @__PURE__ */ jsx7(TabList, __spreadProps(__spreadValues({}, rest), { children: flattened.map((child) => {
          return child;
        }) }))
      }
    ),
    overflowing && /* @__PURE__ */ jsx7("div", { className: "r8-tabs--responsive-select-wrap", children: /* @__PURE__ */ jsx7(
      "select",
      {
        value: activeId != null ? activeId : void 0,
        onChange: (e) => {
          context.setActiveId(e.target.value);
        },
        className: "r8-tabs--responsive-select",
        children: flattened.map((tab) => {
          var _a2, _b, _c, _d, _e;
          if (!((_a2 = tab == null ? void 0 : tab.props) == null ? void 0 : _a2.id)) return null;
          if (!((_b = tab == null ? void 0 : tab.props) == null ? void 0 : _b.children)) return null;
          return /* @__PURE__ */ jsx7("option", { value: (_d = tab.props) == null ? void 0 : _d.id, children: (_e = tab.props) == null ? void 0 : _e.children }, (_c = tab.props) == null ? void 0 : _c.id);
        })
      }
    ) })
  ] });
}
var TabList = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { size, variant, className, responsive } = _b, rest = __objRest(_b, ["size", "variant", "className", "responsive"]);
    const listClass = cn(tabs({ size, variant }), className);
    if (responsive)
      return /* @__PURE__ */ jsx7(ResponsiveTabList, __spreadValues({ className: listClass }, rest));
    return /* @__PURE__ */ jsx7(AKTabList, __spreadProps(__spreadValues({ className: listClass }, rest), { ref }));
  }
);
var Tab = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className, startIcon, badge: badge2, children } = _b, rest = __objRest(_b, ["className", "startIcon", "badge", "children"]);
    return /* @__PURE__ */ jsxs4(AKTab, __spreadProps(__spreadValues({ className: cn("r8-tabs__tab", className) }, rest), { ref, children: [
      startIcon && /* @__PURE__ */ jsx7("span", { className: "r8-tabs__start-icon", children: startIcon }),
      /* @__PURE__ */ jsx7("span", { children }),
      badge2 && /* @__PURE__ */ jsx7("span", { className: "r8-tabs__badge", children: badge2 })
    ] }));
  }
);
var TabPanel = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx7(
      AKTabPanel,
      __spreadProps(__spreadValues({
        className: cn("r8-tabs__content", className)
      }, rest), {
        ref
      })
    );
  }
);

// src/components/glyph-loader/glyph-loader.gif
var glyph_loader_default = "data:image/gif;base64,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";

// src/components/glyph-loader/index.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function GlyphLoader({
  screenReaderLabel
}) {
  return /* @__PURE__ */ jsxs5("div", { role: "status", className: "r8-glyph-loader", children: [
    screenReaderLabel ? /* @__PURE__ */ jsx8("span", { className: "sr-only", children: screenReaderLabel }) : null,
    /* @__PURE__ */ jsx8("img", { className: "r8-glyph-loader-img", src: glyph_loader_default })
  ] });
}

// src/components/select/index.tsx
import {
  Select as AKSelect,
  SelectGroup as AKSelectGroup,
  SelectGroupLabel as AKSelectGroupLabel,
  SelectItem as AKSelectItem,
  SelectItemCheck as AKSelectItemCheck,
  SelectList as AKSelectList,
  SelectPopover as AKSelectPopover,
  SelectRow as AKSelectRow,
  SelectProvider,
  useSelectContext,
  useSelectStore,
  SelectLabel
} from "@ariakit/react";
import { Check } from "@phosphor-icons/react";
import { jsx as jsx9 } from "react/jsx-runtime";
var Select = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx9(AKSelect, __spreadProps(__spreadValues({}, rest), { className: cn("r8-select", className), ref }));
  }
);
var SelectPopover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectPopover,
    __spreadProps(__spreadValues({
      className: cn("r8-popover", className)
    }, rest), {
      getAnchorRect: (el) => {
        var _a2, _b2;
        const inFormGroup = (_a2 = el == null ? void 0 : el.parentElement) == null ? void 0 : _a2.classList.contains(
          "r8-form-group__input"
        );
        if (inFormGroup) {
          const parentFormGroup = el == null ? void 0 : el.closest(".r8-form-group__control");
          return (_b2 = parentFormGroup == null ? void 0 : parentFormGroup.getBoundingClientRect()) != null ? _b2 : el == null ? void 0 : el.getBoundingClientRect();
        }
        return el == null ? void 0 : el.getBoundingClientRect();
      },
      ref,
      children
    })
  );
});
var SelectItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, intent } = _b, rest = __objRest(_b, ["className", "intent"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx9(AKSelectItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var SelectGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var SelectGroupLabel = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectGroupLabel,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group-label", className)
    }, rest), {
      ref,
      children
    })
  );
});
var SelectItemCheck = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectItemCheck,
    __spreadProps(__spreadValues({
      className: cn("r8-select__item-check", className)
    }, rest), {
      ref,
      children: /* @__PURE__ */ jsx9(Check, { weight: "bold" })
    })
  );
});
var SelectRow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectRow,
    __spreadProps(__spreadValues({
      className: cn("r8-select__row", className)
    }, rest), {
      ref
    })
  );
});
var SelectList = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx9(
    AKSelectList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref
    })
  );
});

// src/components/popover/index.tsx
import {
  Popover as AKPopover,
  PopoverAnchor as AKPopoverAnchor,
  PopoverArrow as AKPopoverArrow,
  PopoverDisclosure as AKPopoverDisclosure,
  PopoverDismiss,
  PopoverDescription,
  PopoverHeading,
  PopoverProvider,
  usePopoverContext,
  usePopoverStore
} from "@ariakit/react";
import { jsx as jsx10 } from "react/jsx-runtime";
var PopoverAnchor = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx10(AKPopoverAnchor, __spreadProps(__spreadValues({}, props), { ref }));
});
var PopoverArrow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx10(
    AKPopoverArrow,
    __spreadProps(__spreadValues({
      className: cn("r8-popover__arrow", className)
    }, rest), {
      ref
    })
  );
});
var Popover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx10(AKPopover, __spreadProps(__spreadValues({ className: cn("r8-popover", className) }, rest), { ref, children }));
});
var PopoverDisclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx10(
    AKPopoverDisclosure,
    __spreadProps(__spreadValues({
      className: cn("r8-popover__disclosure", className)
    }, rest), {
      ref
    })
  );
});

// src/components/dialog/index.tsx
import {
  Dialog as AKDialog,
  DialogDescription as AKDialogDescription,
  DialogDisclosure as AKDialogDisclosure,
  DialogDismiss,
  DialogHeading as AKDialogHeading,
  DialogProvider,
  useDialogContext,
  useDialogStore
} from "@ariakit/react";
import { jsx as jsx11 } from "react/jsx-runtime";
var Dialog = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, side, variant, size } = _b, rest = __objRest(_b, ["className", "side", "variant", "size"]);
  const dialogClass = cn(dialog({ variant, side, size, className }));
  const backdropclass = cn("r8-backdrop");
  return /* @__PURE__ */ jsx11(
    AKDialog,
    __spreadProps(__spreadValues({
      backdrop: /* @__PURE__ */ jsx11("div", { className: backdropclass }),
      className: dialogClass
    }, rest), {
      ref
    })
  );
});
var DialogDisclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  if (!dialog)
    throw new Error("DialogDisclosure must be used within a DialogProvider");
  return /* @__PURE__ */ jsx11(
    AKDialogDisclosure,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__trigger", className)
    }, rest), {
      ref
    })
  );
});
var DialogHeading = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx11(
    AKDialogHeading,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__heading", className)
    }, rest), {
      ref
    })
  );
});
var DialogDescription = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx11(
    AKDialogDescription,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__description", className)
    }, rest), {
      ref
    })
  );
});

// src/components/menu/index.tsx
import {
  Menu as AKMenu,
  MenuButton as AKMenuButton,
  MenuGroup as AKMenuGroup,
  MenuGroupLabel as AKMenuGroupLabel,
  MenuItem as AKMenuItem,
  MenuItemCheck as AKMenuItemCheck,
  MenuItemCheckbox as AKMenuItemCheckbox,
  MenuItemRadio as AKMenuItemRadio,
  MenuList as AKMenuList,
  MenuSeparator as AKMenuSeparator,
  MenuProvider,
  useMenuStore,
  useMenuContext
} from "@ariakit/react";
import { Check as Check2 } from "@phosphor-icons/react";
import { jsx as jsx12 } from "react/jsx-runtime";
var Menu = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    const menuClass = cn("r8-popover", className);
    return /* @__PURE__ */ jsx12(AKMenu, __spreadProps(__spreadValues({}, rest), { className: menuClass, ref }));
  }
);
var MenuButton = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx12(AKMenuButton, __spreadProps(__spreadValues({}, props), { ref }));
});
var MenuItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { intent, className } = _b, rest = __objRest(_b, ["intent", "className"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx12(AKMenuItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var MenuSeparator = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx12(
    AKMenuSeparator,
    __spreadProps(__spreadValues({
      className: cn("r8-select__separator", props.className)
    }, props), {
      ref
    })
  );
});
var MenuItemCheckbox = fixedForwardRef(function MenuItemCheckbox2(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  const itemClass = cn(selectItem({ className }));
  return /* @__PURE__ */ jsx12(AKMenuItemCheckbox, __spreadProps(__spreadValues({ ref }, rest), { className: itemClass }));
});
var MenuItemCheck = fixedForwardRef(function MenuItemCheck2(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx12(
    AKMenuItemCheck,
    __spreadProps(__spreadValues({
      className: cn("r8-select__item-check", className),
      ref
    }, rest), {
      children: /* @__PURE__ */ jsx12(Check2, { weight: "bold" })
    })
  );
});
var MenuGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx12(
    AKMenuGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var MenuGroupLabel = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx12(
    AKMenuGroupLabel,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group-label", className)
    }, rest), {
      ref,
      children
    })
  );
});
var MenuList = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx12(
    AKMenuList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref
    })
  );
});
var MenuItemRadio = fixedForwardRef(function MenuItemCheckbox3(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  const itemClass = cn(selectItem({ className }));
  return /* @__PURE__ */ jsx12(AKMenuItemRadio, __spreadProps(__spreadValues({ ref }, rest), { className: itemClass }));
});

// src/components/combobox/index.tsx
import {
  Combobox as AKCombobox,
  ComboboxGroup as AKComboboxGroup,
  ComboboxGroupLabel as AKComboboxGroupLabel,
  ComboboxItem as AKComboboxItem,
  ComboboxList as AKComboboxList,
  ComboboxPopover as AKComboboxPopover,
  ComboboxProvider,
  useComboboxContext,
  useComboboxStore,
  ComboboxLabel
} from "@ariakit/react";
import { jsx as jsx13 } from "react/jsx-runtime";
var Combobox = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx13(AKCombobox, __spreadProps(__spreadValues({ className: cn("r8-select", className) }, rest), { ref }));
});
var ComboboxPopover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx13(
    AKComboboxPopover,
    __spreadProps(__spreadValues({
      className: cn("r8-popover", className)
    }, rest), {
      ref,
      getAnchorRect: (el) => {
        var _a2, _b2;
        const inFormGroup = (_a2 = el == null ? void 0 : el.parentElement) == null ? void 0 : _a2.classList.contains(
          "r8-form-group__input"
        );
        if (inFormGroup) {
          const parentFormGroup = el == null ? void 0 : el.closest(".r8-form-group__control");
          return (_b2 = parentFormGroup == null ? void 0 : parentFormGroup.getBoundingClientRect()) != null ? _b2 : el == null ? void 0 : el.getBoundingClientRect();
        }
        return el == null ? void 0 : el.getBoundingClientRect();
      },
      children
    })
  );
});
var ComboboxItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, intent } = _b, rest = __objRest(_b, ["className", "intent"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx13(AKComboboxItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var ComboboxGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx13(
    AKComboboxGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var ComboboxGroupLabel = fixedForwardRef(({ children, className }, ref) => {
  return /* @__PURE__ */ jsx13(
    AKComboboxGroupLabel,
    {
      className: cn("r8-select__group-label", className),
      ref,
      children
    }
  );
});
var ComboboxList = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx13(
    AKComboboxList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref,
      children
    })
  );
});

// src/components/radio-group/index.tsx
import {
  useRadioStore,
  RadioGroup as AKRadioGroup,
  Radio as AKRadio,
  RadioProvider,
  useRadioContext
} from "@ariakit/react";
import { jsx as jsx14 } from "react/jsx-runtime";
var RadioGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx14(
    AKRadioGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-radio-group", className)
    }, props), {
      ref
    })
  );
});
var Radio = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx14(
      AKRadio,
      __spreadValues({
        className: cn("r8-radio-group__item", className),
        ref
      }, props)
    );
  }
);

// src/components/tooltip/index.tsx
import {
  Tooltip as AKTooltip,
  TooltipAnchor as AKTooltipAnchor,
  TooltipArrow as AKTooltipArrow,
  TooltipProvider,
  useTooltipStore
} from "@ariakit/react";
import { jsx as jsx15 } from "react/jsx-runtime";
var TooltipAnchor = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx15(AKTooltipAnchor, __spreadProps(__spreadValues({}, props), { ref }));
});
var TooltipArrow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx15(
    AKTooltipArrow,
    __spreadProps(__spreadValues({
      className: cn("r8-tooltip__arrow", className)
    }, rest), {
      ref
    })
  );
});
var Tooltip = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx15(AKTooltip, __spreadValues({ ref, className: cn("r8-tooltip", className) }, props));
});

// src/components/disclosure/index.tsx
import {
  DisclosureProvider,
  Disclosure as AKDisclosure,
  DisclosureContent as AKDisclosureContent,
  useDisclosureContext,
  useDisclosureStore
} from "@ariakit/react";
import { jsx as jsx16 } from "react/jsx-runtime";
var Disclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { children } = _b, rest = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx16(AKDisclosure, __spreadProps(__spreadValues({ className: "r8-disclosure" }, rest), { ref, children }));
});
var DisclosureContent = fixedForwardRef((_a, ref) => {
  var _b = _a, { children } = _b, rest = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx16(AKDisclosureContent, __spreadProps(__spreadValues({ className: "r8-disclosure--content", ref }, rest), { children }));
});

// src/components/form/index.tsx
import { CaretDown } from "@phosphor-icons/react";
import { cva as cva4 } from "class-variance-authority";
import { createContext, forwardRef as forwardRef5, useContext } from "react";
import { jsx as jsx17, jsxs as jsxs6 } from "react/jsx-runtime";
var variants = cva4("r8-form-group", {
  variants: {
    size: {
      sm: "r8-form-group--sm",
      md: "r8-form-group--md",
      lg: "r8-form-group--lg"
    },
    severity: {
      error: "r8-form-group--error",
      success: "r8-form-group--success"
    }
  }
});
var FormGroupContext = createContext(null);
var useFormGroupContext = () => {
  const context = useContext(FormGroupContext);
  if (!context) {
    throw new Error("FormGroupControl must be used within a FormGroup");
  }
  return context;
};
function FormGroup({
  children,
  size = "md",
  /**
   * ID is required to associate the label with the input element, as well
   * as the help text.
   */
  id,
  severity
}) {
  const className = variants({ size, severity });
  return /* @__PURE__ */ jsx17(FormGroupContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx17("div", { className, children }) });
}
function FormGroupLabel(props) {
  const { id } = useFormGroupContext();
  return /* @__PURE__ */ jsx17("label", __spreadProps(__spreadValues({ htmlFor: id }, props), { className: "r8-form-group__label", children: props.children }));
}
function FormGroupControl({
  input,
  startAdornment,
  endAdornment,
  endAdornmentOptions
}) {
  return /* @__PURE__ */ jsxs6("div", { className: "r8-form-group__control", children: [
    startAdornment && /* @__PURE__ */ jsx17("div", { className: "r8-form-group__adornment r8-form-group__adornment--start", children: startAdornment }),
    /* @__PURE__ */ jsx17("div", { className: "r8-form-group__input", children: input }),
    endAdornment && /* @__PURE__ */ jsx17(
      "div",
      {
        className: `r8-form-group__adornment r8-form-group__adornment--end ${(endAdornmentOptions == null ? void 0 : endAdornmentOptions.absolute) ? "r8-form-group__adornment--absolute" : ""} ${(endAdornmentOptions == null ? void 0 : endAdornmentOptions.inert) ? "r8-form-group__adornment--inert" : ""}`,
        children: endAdornment
      }
    )
  ] });
}
function FormGroupHelpText({ children }) {
  const { id } = useFormGroupContext();
  return /* @__PURE__ */ jsx17("div", { id: `help-text-${id}`, className: "r8-form-group__help-text", children });
}
FormGroup.Label = FormGroupLabel;
FormGroup.Control = FormGroupControl;
FormGroup.HelpText = FormGroupHelpText;
var TextField = forwardRef5(
  (_a, ref) => {
    var _b = _a, {
      size,
      severity,
      label,
      helpText,
      errorText,
      startAdornment,
      endAdornment,
      id
    } = _b, inputProps = __objRest(_b, [
      "size",
      "severity",
      "label",
      "helpText",
      "errorText",
      "startAdornment",
      "endAdornment",
      "id"
    ]);
    const helpTextId = `help-text-${id}`;
    const textToShow = errorText || helpText;
    return /* @__PURE__ */ jsxs6(FormGroup, { id, size, severity: errorText ? "error" : severity, children: [
      label ? /* @__PURE__ */ jsx17(FormGroup.Label, { htmlFor: id, children: label }) : null,
      /* @__PURE__ */ jsx17(
        FormGroup.Control,
        {
          startAdornment,
          endAdornment,
          input: /* @__PURE__ */ jsx17(
            "input",
            __spreadValues({
              id,
              "aria-describedby": helpTextId,
              ref
            }, inputProps)
          )
        }
      ),
      textToShow ? /* @__PURE__ */ jsx17(FormGroup.HelpText, { children: textToShow }) : null
    ] });
  }
);
var NativeSelect = forwardRef5(
  (_a, ref) => {
    var _b = _a, {
      size,
      severity,
      label,
      helpText,
      errorText,
      startAdornment,
      id
    } = _b, inputProps = __objRest(_b, [
      "size",
      "severity",
      "label",
      "helpText",
      "errorText",
      "startAdornment",
      "id"
    ]);
    const helpTextId = `help-text-${id}`;
    const textToShow = errorText || helpText;
    return /* @__PURE__ */ jsxs6(FormGroup, { id, size, severity: errorText ? "error" : severity, children: [
      label ? /* @__PURE__ */ jsx17(FormGroup.Label, { htmlFor: id, children: label }) : null,
      /* @__PURE__ */ jsx17(
        FormGroup.Control,
        {
          startAdornment,
          endAdornment: /* @__PURE__ */ jsx17("div", { className: "r8-inert", children: /* @__PURE__ */ jsx17(CaretDown, { size: 12, weight: "bold" }) }),
          endAdornmentOptions: {
            absolute: true,
            inert: true
          },
          input: /* @__PURE__ */ jsx17(
            "select",
            __spreadProps(__spreadValues({
              id,
              "aria-describedby": helpTextId
            }, inputProps), {
              ref
            })
          )
        }
      ),
      textToShow ? /* @__PURE__ */ jsx17(FormGroup.HelpText, { children: textToShow }) : null
    ] });
  }
);
var Textarea = forwardRef5(
  (_a, ref) => {
    var _b = _a, { size, severity, label, helpText, errorText, id } = _b, inputProps = __objRest(_b, ["size", "severity", "label", "helpText", "errorText", "id"]);
    {
      const helpTextId = `help-text-${id}`;
      const textToShow = errorText || helpText;
      return /* @__PURE__ */ jsxs6(
        FormGroup,
        {
          id,
          size,
          severity: errorText ? "error" : severity,
          children: [
            label ? /* @__PURE__ */ jsx17(FormGroup.Label, { htmlFor: id, children: label }) : null,
            /* @__PURE__ */ jsx17(
              FormGroup.Control,
              {
                input: /* @__PURE__ */ jsx17(
                  "textarea",
                  __spreadProps(__spreadValues({
                    id,
                    "aria-describedby": helpTextId
                  }, inputProps), {
                    ref
                  })
                ),
                startAdornment: null,
                endAdornment: null
              }
            ),
            textToShow ? /* @__PURE__ */ jsx17(FormGroup.HelpText, { children: textToShow }) : null
          ]
        }
      );
    }
  }
);

// src/components/badge/index.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx18, jsxs as jsxs7 } from "react/jsx-runtime";
var Badge = forwardRef6(
  ({ variant, startIcon, children, size = "md", rounded }, ref) => {
    const className = cn(
      badge({ variant, size }),
      rounded ? "r8-badge--rounded" : null
    );
    return /* @__PURE__ */ jsxs7("div", { ref, className, children: [
      startIcon ? /* @__PURE__ */ jsx18("span", { className: "r8-badge__icon", children: startIcon }) : null,
      /* @__PURE__ */ jsx18("span", { children })
    ] });
  }
);
Badge.displayName = "Badge";

// src/components/box/index.tsx
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx19 } from "react/jsx-runtime";
var Box = forwardRef7(
  (_a, ref) => {
    var _b = _a, { variant, children, className } = _b, rest = __objRest(_b, ["variant", "children", "className"]);
    const boxClass = cn(box({ variant }), className);
    return /* @__PURE__ */ jsx19("div", __spreadProps(__spreadValues({ ref, className: boxClass }, rest), { children }));
  }
);
Box.displayName = "Box";

// src/components/goo/index.tsx
import { useRafInterval, useSize } from "ahooks";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect as useEffect2, useRef as useRef2, useState as useState2 } from "react";

// src/components/goo/goo-fallback.jpg
var goo_fallback_default = "./goo-fallback-MSJU2ZUC.jpg";

// src/components/goo/index.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
function Goo({
  speed,
  resolution,
  depth,
  seed = 0,
  still = false,
  variant = "color"
}) {
  const wrapRef = useRef2(null);
  const inView = useInView(wrapRef);
  const size = useSize(wrapRef);
  return /* @__PURE__ */ jsx20(
    motion.div,
    {
      ref: wrapRef,
      id: "goo-wrap",
      "aria-hidden": "true",
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: still ? 0 : 0.5 },
      className: "goo-wrapper",
      children: size ? /* @__PURE__ */ jsx20(
        GooCanvas,
        {
          speed,
          seed,
          resolution,
          depth,
          size,
          inView,
          still,
          variant
        }
      ) : null
    }
  );
}
function GooCanvas({
  inView,
  size,
  speed,
  resolution,
  depth,
  seed,
  still,
  variant
}) {
  const mousePosRef = useRef2({ x: 0, y: 0 });
  const startRef = useRef2(performance.now());
  const canvasRef = useRef2(null);
  const glRef = useRef2(null);
  const shaderProgramRef = useRef2(null);
  const reducedMotion = useReducedMotion();
  if (typeof window === "undefined") {
    console.error(
      "The goo component cannot be server-side rendered as it requires WebGL and browser APIs"
    );
    return null;
  }
  const [invert, setInvert] = useState2(
    () => {
      var _a;
      return ((_a = window.matchMedia) == null ? void 0 : _a.call(window, "(prefers-color-scheme: dark)").matches) ? -1 : 1;
    }
  );
  useEffect2(() => {
    if (variant !== "contour") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      setInvert(e.matches ? -1 : 1);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [variant]);
  const [tint, setTint] = useState2(invert ? [1, 1, 1] : [0, 0, 0]);
  useEffect2(() => {
    if (variant !== "contour") return;
    if (canvasRef.current && invert) {
      const colorString = getComputedStyle(canvasRef.current).getPropertyValue(
        "color"
      );
      const rgb = colorString.match(/\d+/g);
      if (rgb) {
        setTint(rgb.map((c) => Number.parseInt(c) / 255));
      }
    }
  }, [invert, variant]);
  const vertexShaderSource = `
	attribute vec2 position;
	void main() {
			gl_Position = vec4(position, 0.0, 1.0);
	}
`;
  const maxDepth = Math.min(Math.max(depth, 1), 20);
  const fragmentShaderSource = variant === "contour" ? `
#extension GL_OES_standard_derivatives : enable

precision mediump float;
uniform vec2 iResolution;
uniform float iTime;
uniform vec2 iMouse;
uniform float iInvert;
uniform vec3 iTint;

vec3 colorBlack = vec3(0.0, 0.0, 0.0);
vec3 colorWhite = vec3(1.0, 1.0, 1.0);

vec2 effect(vec2 p, float i, float time) {
    float speed = ${speed.toFixed(2)};
    return vec2(sin(p.x * i + (time * speed)) * cos(p.y * i + (time * speed)), sin(length(p.x)) * cos(length(p.y)));
}

void main() {
    vec2 p = (gl_FragCoord.xy - iResolution.xy) / max(iResolution.x, iResolution.y);
    p.x += ${seed.toFixed(1)};
    p.y += ${seed.toFixed(1)};
    
    p *= ${resolution.toFixed(1)};
    for (int i = 1; i < ${maxDepth}; i++) {
        float fi = float(i);
        p += effect(p, fi, iTime);
    }
    vec3 noise = mix(mix(colorBlack, colorWhite, 1.0-sin(p.x)), colorBlack, cos(p.y+p.x));

    float spacing = 1.0 / (iResolution.x * 0.0025);
    float lines = mod(noise.r, spacing) / spacing;
    lines = min(lines * 2.0, 1.0) - max(lines * 2.0 - 1.0, 0.0);
    lines /= fwidth(noise.r / spacing);
    
    float weight = smoothstep(0.0, 1.0, gl_FragCoord.y / iResolution.y);
    weight = mix(2.0, 5.0, weight);
    
    lines -= weight;
    float alpha = clamp(1.0 - lines, 0.0, 1.0);
    
    gl_FragColor = vec4(iTint, alpha);
}
` : `
precision mediump float;
uniform vec2 iResolution;
uniform float iTime;
uniform vec2 iMouse;

vec3 color1 = vec3(235.0/255.0, 231.0/255.0, 92.0/255.0);
vec3 color2 = vec3(223.0/255.0, 72.0/255.0, 67.0/255.0);
vec3 color3 = vec3(235.0/255.0, 64.0/255.0, 240.0/255.0);

vec2 effect(vec2 p, float i, float time) {
    float speed = ${speed.toFixed(2)};
    return vec2(sin(p.x * i + (time * speed)) * cos(p.y * i + (time * speed)), sin(length(p.x)) * cos(length(p.y)));
}

void main() {
    vec2 p = (2.0 * gl_FragCoord.xy - iResolution.xy) / max(iResolution.x, iResolution.y);
    p.x += ${seed.toFixed(1)};
    p.y += ${seed.toFixed(1)};
    
    p *= ${resolution.toFixed(1)};
    for (int i = 1; i < ${maxDepth}; i++) {
        float fi = float(i);
        p += effect(p, fi, iTime);
    }
    vec3 col = mix(mix(color1, color2, 1.0-sin(p.x)), color3, cos(p.y+p.x));
    gl_FragColor = vec4(col, 1.0);
}
`;
  useEffect2(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", {
      preserveDrawingBuffer: still,
      antialias: true
    }) || canvas.getContext("experimental-webgl");
    if (!gl) {
      console.error(
        "Unable to initialize WebGL. Your browser may not support it."
      );
      return;
    }
    if (variant === "contour") {
      const ext = gl.getExtension("OES_standard_derivatives");
      if (!ext) {
        console.warn("OES_standard_derivatives not supported");
      }
    }
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    if (!vertexShader) return;
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);
    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      console.error(
        "Vertex shader compilation error:",
        gl.getShaderInfoLog(vertexShader)
      );
      gl.deleteShader(vertexShader);
      return;
    }
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    if (!fragmentShader) return;
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);
    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      console.error(
        "Fragment shader compilation error:",
        gl.getShaderInfoLog(fragmentShader)
      );
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return;
    }
    const shaderProgram = gl.createProgram();
    if (!shaderProgram) return;
    gl.attachShader(shaderProgram, vertexShader);
    gl.attachShader(shaderProgram, fragmentShader);
    gl.linkProgram(shaderProgram);
    if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
      console.error(
        "Shader program linking error:",
        gl.getProgramInfoLog(shaderProgram)
      );
      gl.deleteProgram(shaderProgram);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return;
    }
    gl.useProgram(shaderProgram);
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = [
      -1,
      -1,
      1,
      -1,
      -1,
      1,
      -1,
      1,
      1,
      -1,
      1,
      1
    ];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
    const positionLocation = gl.getAttribLocation(shaderProgram, "position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    glRef.current = gl;
    shaderProgramRef.current = shaderProgram;
  }, [variant, speed, resolution, seed, maxDepth]);
  useEffect2(() => {
    const gl = glRef.current;
    const shaderProgram = shaderProgramRef.current;
    const { width, height } = size;
    if (!gl || !shaderProgram) return;
    gl.viewport(0, 0, width * devicePixelRatio, height * devicePixelRatio);
    const iResolutionLocation = gl.getUniformLocation(
      shaderProgram,
      "iResolution"
    );
    gl.uniform2f(iResolutionLocation, gl.canvas.width, gl.canvas.height);
  }, [size]);
  const frameHandler = () => {
    const start = startRef.current;
    const time = performance.now() - start;
    const mousePos = mousePosRef.current;
    if ((reducedMotion || still) && time >= 50) {
      return;
    }
    const gl = glRef.current;
    const shaderProgram = shaderProgramRef.current;
    if (!gl || !shaderProgram) return;
    const iTimeLocation = gl.getUniformLocation(shaderProgram, "iTime");
    const iMouseLocation = gl.getUniformLocation(shaderProgram, "iMouse");
    gl.uniform2f(iMouseLocation, mousePos.x, mousePos.y);
    gl.uniform1f(iTimeLocation, time * 1e-3);
    if (variant === "contour") {
      const iTintLocation = gl.getUniformLocation(shaderProgram, "iTint");
      const iInvertLocation = gl.getUniformLocation(shaderProgram, "iInvert");
      gl.uniform3f(iTintLocation, tint[0], tint[1], tint[2]);
      gl.uniform1f(iInvertLocation, invert);
    }
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  };
  const devicePixelRatio = window.devicePixelRatio || 1;
  useRafInterval(frameHandler, inView ? 1e3 / 60 : void 0);
  return /* @__PURE__ */ jsx20(
    "canvas",
    {
      width: size.width * devicePixelRatio,
      height: size.height * devicePixelRatio,
      ref: canvasRef,
      className: "goo-canvas",
      style: variant === "color" ? { backgroundImage: `url(${goo_fallback_default})` } : void 0
    }
  );
}

// src/components/site-navigation/index.tsx
import * as Ariakit from "@ariakit/react";
import { WebhooksLogo } from "@phosphor-icons/react/dist/ssr";
import { ArrowsOut } from "@phosphor-icons/react/dist/ssr/ArrowsOut";
import { CaretDown as CaretDown2 } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { CaretRight as CaretRight2 } from "@phosphor-icons/react/dist/ssr/CaretRight";
import { CubeFocus } from "@phosphor-icons/react/dist/ssr/CubeFocus";
import { GraduationCap } from "@phosphor-icons/react/dist/ssr/GraduationCap";
import { Image } from "@phosphor-icons/react/dist/ssr/Image";
import { ImageSquare } from "@phosphor-icons/react/dist/ssr/ImageSquare";
import { List } from "@phosphor-icons/react/dist/ssr/List";
import { Microphone } from "@phosphor-icons/react/dist/ssr/Microphone";
import { Plus } from "@phosphor-icons/react/dist/ssr/Plus";
import { Rocket } from "@phosphor-icons/react/dist/ssr/Rocket";
import { Sparkle } from "@phosphor-icons/react/dist/ssr/Sparkle";
import { TextT } from "@phosphor-icons/react/dist/ssr/TextT";
import { Video } from "@phosphor-icons/react/dist/ssr/Video";
import {
  useMemo,
  useState as useState3,
  useRef as useRef3,
  forwardRef as forwardRef8,
  useEffect as useEffect3
} from "react";

// src/components/site-navigation/util.tsx
import { Lightning } from "@phosphor-icons/react";
import { jsx as jsx21 } from "react/jsx-runtime";
var LinksObjectData = {
  dashboard: {
    label: "Dashboard",
    href: "/dashboard",
    primary: true,
    matchPatterns: ["/dashboard/*"]
  },
  signup: {
    label: "Try for free",
    href: "/signin",
    primary: true,
    icon: /* @__PURE__ */ jsx21(Lightning, { size: 18 })
  },
  signin: {
    label: "Sign in",
    href: "/signin"
  },
  explore: {
    label: "Explore",
    href: "/explore"
  },
  pricing: {
    label: "Pricing",
    href: "/pricing"
  },
  docs: {
    label: "Docs",
    href: "/docs",
    matchPatterns: ["/docs/*"]
  },
  enterprise: {
    label: "Enterprise",
    href: "/enterprise"
  },
  blog: {
    label: "Blog",
    href: "/blog",
    matchPatterns: ["/blog/*"]
  },
  changelog: {
    label: "Changelog",
    href: "/changelog",
    matchPatterns: ["/changelog/*"]
  },
  playground: {
    label: "Playground",
    href: "/playground"
  }
};
var Links = LinksObjectData;
function isItemActive(item, currentPath) {
  var _a;
  if (item.href === currentPath) return true;
  if (currentPath.startsWith(`${item.href}/`) && item.href !== "/") return true;
  if ((_a = item.matchPatterns) == null ? void 0 : _a.some((pattern) => {
    if (pattern.includes("*")) {
      const regex = new RegExp(`^${pattern.replace(/\*/g, ".*")}$`);
      return regex.test(currentPath);
    }
    return currentPath.startsWith(pattern);
  }))
    return true;
  return false;
}

// src/components/site-navigation/index.tsx
import { BookOpen, GridNine, Question, TextAa } from "@phosphor-icons/react";
import { Fragment, jsx as jsx22, jsxs as jsxs8 } from "react/jsx-runtime";
var dashboardMenu = [
  {
    label: "Dashboard",
    description: "View your Replicate dashboard",
    href: "/dashboard",
    icon: /* @__PURE__ */ jsx22(BookOpen, { size: 18 })
  },
  {
    label: "Models",
    description: "View and run your models",
    href: "/models",
    icon: /* @__PURE__ */ jsx22(CubeFocus, { size: 18 })
  },
  {
    label: "Predictions",
    description: "Review predictions you've run",
    href: "/predictions",
    internal: true,
    icon: /* @__PURE__ */ jsx22(Sparkle, { size: 18 })
  },
  {
    label: "Trainings",
    description: "Models you've created from datasets like images or videos",
    href: "/trainings",
    icon: /* @__PURE__ */ jsx22(GraduationCap, { size: 18 })
  },
  {
    label: "Deployments",
    description: "Your private AI models in the cloud",
    href: "/deployments",
    icon: /* @__PURE__ */ jsx22(Rocket, { size: 18 })
  },
  {
    label: "Webhooks",
    description: "Review webhooks for your predictions",
    href: "/webhooks",
    icon: /* @__PURE__ */ jsx22(WebhooksLogo, { size: 18 })
  },
  {
    label: "Support",
    description: "Reach out to the Replicate team for support",
    href: "/support",
    icon: /* @__PURE__ */ jsx22(Question, { size: 18 })
  }
];
var exploreMenu = [
  {
    title: "Explore models",
    items: [
      {
        icon: /* @__PURE__ */ jsx22(Image, { size: 18 }),
        label: "Image models",
        href: "/collections/text-to-image"
      },
      {
        icon: /* @__PURE__ */ jsx22(Video, { size: 18 }),
        label: "Video models",
        href: "/collections/text-to-video"
      },
      {
        icon: /* @__PURE__ */ jsx22(TextT, { size: 18 }),
        label: "Language models",
        href: "/collections/language-models"
      },
      {
        icon: /* @__PURE__ */ jsx22(CaretRight2, { size: 18 }),
        label: "Explore all",
        href: "/explore",
        className: "v2-menu-item-small"
      }
    ]
  },
  {
    title: "Create models",
    items: [
      {
        icon: /* @__PURE__ */ jsx22(Plus, { size: 18 }),
        label: "Push a model",
        internal: true,
        href: "/docs/guides/push-a-model"
      },
      {
        icon: /* @__PURE__ */ jsx22(GraduationCap, { size: 18 }),
        label: "Train a model",
        internal: true,
        href: "/docs/get-started/fine-tune-with-flux"
      },
      {
        icon: /* @__PURE__ */ jsx22(Rocket, { size: 18 }),
        label: "Create deployment",
        internal: true,
        href: "/docs/topics/deployments/create-a-deployment"
      },
      {
        icon: /* @__PURE__ */ jsx22(CaretRight2, { size: 16 }),
        label: "View the docs",
        internal: true,
        href: "/docs",
        className: "v2-menu-item-small"
      }
    ]
  },
  {
    title: "Tasks",
    items: [
      {
        icon: /* @__PURE__ */ jsx22(ArrowsOut, { size: 18 }),
        label: "Upscale images",
        href: "/collections/super-resolution"
      },
      {
        icon: /* @__PURE__ */ jsx22(ImageSquare, { size: 18 }),
        label: "Remove backgrounds",
        href: "/collections/remove-backgrounds"
      },
      {
        icon: /* @__PURE__ */ jsx22(Microphone, { size: 18 }),
        label: "Transcribe speech",
        href: "/collections/speech-to-text"
      },
      {
        icon: /* @__PURE__ */ jsx22(CaretRight2, { size: 18 }),
        label: "View all tasks",
        href: "/explore#collections",
        className: "v2-menu-item-small"
      }
    ]
  }
];
var baseLinks = [
  Links.explore,
  Links.pricing,
  Links.enterprise,
  Links.docs,
  Links.blog
];
function makeHeaderLinks(authState) {
  let links = [...baseLinks];
  if (authState !== "anonymous") {
    links = [Links.dashboard, ...links];
  } else {
    links = [...links, Links.signin, Links.signup];
  }
  return links;
}
function SiteNavigationInner({
  authState,
  currentPath,
  anchorProps = () => ({})
}) {
  const dashboardMenuStore = Ariakit.useMenuStore();
  const exploreMenuStore = Ariakit.useMenuStore();
  const defaultNavigationItems = useMemo(() => {
    const items = [
      Links.pricing,
      Links.enterprise,
      Links.docs,
      Links.blog
    ];
    if (authState === "anonymous") {
      items.push(Links.signin);
      items.push(Links.signup);
    }
    return items;
  }, [authState]);
  return /* @__PURE__ */ jsxs8(Ariakit.Menubar, { className: "v2-menubar", children: [
    authState === "authenticated" ? /* @__PURE__ */ jsxs8(
      Ariakit.MenuProvider,
      {
        timeout: 0,
        placement: "bottom",
        store: dashboardMenuStore,
        children: [
          /* @__PURE__ */ jsxs8(
            Ariakit.MenuItem,
            {
              tabbable: true,
              className: "v2-dropdown-item group",
              render: /* @__PURE__ */ jsx22(
                Ariakit.MenuButton,
                {
                  onFocusVisible: () => {
                    exploreMenuStore.hide();
                    dashboardMenuStore.show();
                  },
                  toggleOnClick: () => {
                    exploreMenuStore.hide();
                    dashboardMenuStore.show();
                    return false;
                  },
                  render: /* @__PURE__ */ jsx22(
                    "a",
                    __spreadValues({
                      "data-active": isItemActive(Links.dashboard, currentPath),
                      href: Links.dashboard.href
                    }, anchorProps(Links.dashboard))
                  ),
                  showOnHover: true
                }
              ),
              children: [
                /* @__PURE__ */ jsx22("span", { children: "Dashboard" }),
                /* @__PURE__ */ jsx22(CaretDown2, { className: "v2-dropdown-item-caret", weight: "bold" })
              ]
            }
          ),
          /* @__PURE__ */ jsx22(
            Ariakit.Menu,
            {
              hideOnHoverOutside: true,
              focusable: false,
              unmountOnHide: true,
              className: "v2-dropdown-menu v2-dropdown-menu--dashboard",
              children: dashboardMenu.map((item) => /* @__PURE__ */ jsxs8(
                Ariakit.MenuItem,
                {
                  tabbable: true,
                  focusOnHover: false,
                  blurOnHoverEnd: true,
                  className: "v2-dropdown-menu-item",
                  render: /* @__PURE__ */ jsx22(
                    "a",
                    __spreadValues({
                      "data-active": isItemActive(item, currentPath),
                      href: item.href
                    }, anchorProps(item))
                  ),
                  children: [
                    /* @__PURE__ */ jsx22("span", { className: "v2-dropdown-menu-item--icon", children: item.icon }),
                    /* @__PURE__ */ jsxs8("div", { children: [
                      /* @__PURE__ */ jsx22("div", { className: "v2-dropdown-menu-item--label", children: item.label }),
                      /* @__PURE__ */ jsx22("div", { className: "v2-dropdown-menu-item--description", children: item.description })
                    ] })
                  ]
                },
                item.label
              ))
            }
          )
        ]
      }
    ) : null,
    /* @__PURE__ */ jsxs8(
      Ariakit.MenuProvider,
      {
        timeout: 0,
        placement: "bottom",
        store: exploreMenuStore,
        children: [
          /* @__PURE__ */ jsxs8(
            Ariakit.MenuItem,
            {
              tabbable: true,
              className: "v2-dropdown-item group",
              render: /* @__PURE__ */ jsx22(
                Ariakit.MenuButton,
                {
                  showOnHover: true,
                  onFocusVisible: (e) => {
                    dashboardMenuStore.hide();
                    exploreMenuStore.show();
                  },
                  toggleOnClick: () => {
                    dashboardMenuStore.hide();
                    exploreMenuStore.show();
                    return false;
                  },
                  render: /* @__PURE__ */ jsx22(
                    "a",
                    __spreadValues({
                      "data-active": isItemActive(Links.explore, currentPath),
                      href: Links.explore.href
                    }, anchorProps(Links.explore))
                  )
                }
              ),
              children: [
                /* @__PURE__ */ jsx22("span", { children: "Explore" }),
                /* @__PURE__ */ jsx22(CaretDown2, { className: "v2-dropdown-item-caret", weight: "bold" })
              ]
            }
          ),
          /* @__PURE__ */ jsxs8(
            Ariakit.Menu,
            {
              hideOnHoverOutside: true,
              focusable: false,
              unmountOnHide: true,
              className: "v2-dropdown-menu v2-dropdown-menu--explore",
              children: [
                exploreMenu.map((section) => /* @__PURE__ */ jsxs8(
                  "div",
                  {
                    className: "v2-dropdown-menu--explore-section",
                    children: [
                      /* @__PURE__ */ jsx22("h3", { className: "v2-dropdown-menu--explore-section-title", children: section.title }),
                      /* @__PURE__ */ jsx22("ul", { className: "v2-dropdown-menu--explore-section-list", children: section.items.map((item) => {
                        var _a;
                        return /* @__PURE__ */ jsx22("li", { children: /* @__PURE__ */ jsxs8(
                          Ariakit.MenuItem,
                          {
                            tabbable: true,
                            focusOnHover: false,
                            blurOnHoverEnd: true,
                            className: "v2-dropdown-menu-item v2-dropdown-menu-item--explore",
                            render: /* @__PURE__ */ jsx22(
                              "a",
                              __spreadValues({
                                "data-active": isItemActive(item, currentPath),
                                href: item.href
                              }, anchorProps(item))
                            ),
                            children: [
                              /* @__PURE__ */ jsx22("span", { className: "v2-dropdown-menu-item--explore-icon", children: item.icon }),
                              /* @__PURE__ */ jsx22(
                                "span",
                                {
                                  className: `v2-dropdown-menu-item--explore-label ${(_a = item.className) != null ? _a : ""}`,
                                  children: item.label
                                }
                              )
                            ]
                          }
                        ) }, item.label);
                      }) })
                    ]
                  },
                  section.title
                )),
                /* @__PURE__ */ jsx22(
                  Ariakit.MenuItem,
                  {
                    tabbable: true,
                    focusOnHover: false,
                    blurOnHoverEnd: true,
                    className: "v2-dropdown-menu-item v2-dropdown-menu-item--playground",
                    render: /* @__PURE__ */ jsx22(
                      "a",
                      __spreadValues({
                        "data-active": isItemActive(Links.playground, currentPath),
                        href: Links.playground.href
                      }, anchorProps(Links.playground))
                    ),
                    children: /* @__PURE__ */ jsxs8("div", { className: "v2-dropdown-menu-item-playground-wrapper", children: [
                      /* @__PURE__ */ jsx22("div", { className: "v2-dropdown-menu-item-playground-icon", children: /* @__PURE__ */ jsx22(GridNine, {}) }),
                      /* @__PURE__ */ jsxs8("div", { className: "v2-dropdown-menu-item--playground-label", children: [
                        /* @__PURE__ */ jsxs8("span", { className: "v2-playground-label-part-1", children: [
                          "Explore the new",
                          " "
                        ] }),
                        /* @__PURE__ */ jsx22("span", { className: "v2-playground-label-part-2", children: "Playground" }),
                        /* @__PURE__ */ jsx22("span", { className: "v2-beta-badge", children: "Beta" })
                      ] })
                    ] })
                  }
                )
              ]
            }
          )
        ]
      }
    ),
    defaultNavigationItems.map((item) => {
      return /* @__PURE__ */ jsx22(Ariakit.MenuProvider, { timeout: 0, children: /* @__PURE__ */ jsxs8(
        Ariakit.MenuItem,
        {
          tabbable: true,
          focusOnHover: false,
          render: /* @__PURE__ */ jsx22(
            "a",
            __spreadValues({
              className: ` ${item.primary ? "v2-menu-item-primary" : "v2-menu-item"} group`,
              href: item.href,
              "data-active": isItemActive(item, currentPath)
            }, anchorProps(item))
          ),
          children: [
            item.icon,
            /* @__PURE__ */ jsx22("span", { className: "v2-menu-item-label", children: item.label })
          ]
        }
      ) }, item.label);
    })
  ] });
}
function SiteNavigation(props) {
  const {
    authState,
    variant,
    avatar,
    meta,
    logo = "glyph",
    onDisclosureOpenChange = () => {
    },
    anchorProps = () => {
    },
    search
  } = props;
  const links = makeHeaderLinks(authState);
  const headerRef = useRef3(null);
  useEffect3(() => {
    if (variant !== "floating") return;
    const handleScroll = () => {
      if (!headerRef.current) return;
      const scrolled = window.scrollY > 0;
      headerRef.current.setAttribute("data-scrolled", scrolled.toString());
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [variant]);
  return /* @__PURE__ */ jsxs8(
    Ariakit.DisclosureProvider,
    {
      setOpen: (open) => {
        if (open) {
          document.documentElement.scrollTo({
            top: 0,
            behavior: "instant"
          });
        }
        onDisclosureOpenChange(open);
      },
      children: [
        /* @__PURE__ */ jsx22("header", { ref: headerRef, "data-variant": variant, className: "v2-header", children: /* @__PURE__ */ jsxs8("div", { className: "v2-header-inner", children: [
          /* @__PURE__ */ jsx22("div", { className: "v2-header-transparent-backdrop" }),
          /* @__PURE__ */ jsxs8("div", { "data-header-container": true, className: "v2-header-container", children: [
            /* @__PURE__ */ jsxs8("div", { className: "v2-header-wrapper", children: [
              logo === "wordmark" ? /* @__PURE__ */ jsx22(MarketingLogo, {}) : /* @__PURE__ */ jsx22(Logo, {}),
              avatar ? /* @__PURE__ */ jsx22("div", { className: "v2-header-avatar", children: avatar }) : null,
              search ? /* @__PURE__ */ jsx22("div", { className: "v2-header-search", children: search }) : null,
              meta ? /* @__PURE__ */ jsx22("div", { className: "v2-header-meta", children: meta }) : null
            ] }),
            /* @__PURE__ */ jsx22("div", { className: "v2-header-desktop-nav", children: /* @__PURE__ */ jsx22(SiteNavigationInner, __spreadValues({}, props)) }),
            /* @__PURE__ */ jsx22("div", { className: "v2-header-mobile-nav", children: /* @__PURE__ */ jsx22(
              Ariakit.Disclosure,
              {
                render: /* @__PURE__ */ jsx22(
                  Button,
                  {
                    variant: "outlined",
                    size: "sm",
                    startIcon: /* @__PURE__ */ jsx22(List, { weight: "bold" })
                  }
                ),
                children: "Menu"
              }
            ) })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx22(Ariakit.DisclosureContent, { className: "v2-header-mobile-nav-disclosure", children: /* @__PURE__ */ jsx22("div", { className: "v2-header-mobile-nav-disclosure-inner", children: /* @__PURE__ */ jsx22("div", { className: "v2-header-mobile-nav-disclosure-container", children: /* @__PURE__ */ jsxs8("nav", { className: "v2-header-mobile-nav-disclosure-nav", children: [
          links.map((link) => {
            return /* @__PURE__ */ jsxs8(
              "a",
              __spreadProps(__spreadValues({
                href: link.href,
                "data-active": isItemActive(link, props.currentPath)
              }, anchorProps(link)), {
                className: `v2-header-mobile-link ${link.primary ? "v2-header-mobile-link--primary" : ""}`,
                children: [
                  link.icon,
                  link.label
                ]
              }),
              link.label
            );
          }),
          /* @__PURE__ */ jsx22("hr", { className: "v2-header-mobile-divider" }),
          /* @__PURE__ */ jsxs8(
            "a",
            __spreadProps(__spreadValues({
              href: Links.playground.href,
              "data-active": isItemActive(Links.playground, props.currentPath)
            }, anchorProps(Links.playground)), {
              className: "v2-header-mobile-link v2-header-mobile-link--playground",
              children: [
                /* @__PURE__ */ jsxs8("span", { className: "v2-playground-label-part-1", children: [
                  "Explore the new",
                  " "
                ] }),
                /* @__PURE__ */ jsx22("span", { className: "v2-playground-label-part-2", children: "Playground" }),
                /* @__PURE__ */ jsx22("span", { className: "v2-beta-badge", children: "Beta" })
              ]
            }),
            Links.playground.href
          )
        ] }) }) }) })
      ]
    }
  );
}
var GlyphSVG = forwardRef8((props, ref) => {
  return /* @__PURE__ */ jsxs8(
    "svg",
    {
      ref,
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg",
      xmlnsXlink: "http://www.w3.org/1999/xlink",
      xmlSpace: "preserve",
      viewBox: "0 0 1000 1000",
      width: "100%",
      height: "100%",
      fill: "currentColor",
      children: [
        /* @__PURE__ */ jsx22("title", { children: "Replicate glyph" }),
        /* @__PURE__ */ jsxs8("g", { children: [
          /* @__PURE__ */ jsx22("polygon", { points: "1000,427.6 1000,540.6 603.4,540.6 603.4,1000 477,1000 477,427.6" }),
          /* @__PURE__ */ jsx22("polygon", { points: "1000,213.8 1000,327 364.8,327 364.8,1000 238.4,1000 238.4,213.8" }),
          /* @__PURE__ */ jsx22("polygon", { points: "1000,0 1000,113.2 126.4,113.2 126.4,1000 0,1000 0,0" })
        ] })
      ]
    }
  );
});
var WordmarkSVG = forwardRef8((props, ref) => {
  return /* @__PURE__ */ jsxs8(
    "svg",
    {
      ref,
      fill: "currentColor",
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 120",
      children: [
        /* @__PURE__ */ jsx22("title", { children: "Replicate wordmark" }),
        /* @__PURE__ */ jsx22("polygon", { points: "86.96 0 86.96 10.74 12.03 10.74 12.03 95.41 0 95.41 0 0 86.96 0" }),
        /* @__PURE__ */ jsx22("polygon", { points: "86.96 20.37 86.96 31.11 34.75 31.11 34.75 95.41 22.71 95.41 22.71 20.37 86.96 20.37" }),
        /* @__PURE__ */ jsx22("polygon", { points: "86.96 40.67 86.96 51.47 57.46 51.47 57.46 95.41 45.42 95.41 45.42 40.67 86.96 40.67" }),
        /* @__PURE__ */ jsx22("path", { d: "m121.21,39.62c14.32,0,26.35,9.5,26.35,27.16,0,1.17,0,2.16-.12,3.95h-43.02c.56,8.95,7.78,14.87,16.85,14.87,7.65,0,12.78-3.52,15.61-8.33l9.2,6.54c-4.94,7.78-13.45,12.71-24.93,12.71-16.6,0-28.57-11.79-28.57-28.45.06-16.11,12.03-28.45,28.64-28.45m-16.17,22.28h30.43c-1.3-7.9-7.65-12.16-14.69-12.16s-14.13,4.07-15.74,12.16" }),
        /* @__PURE__ */ jsx22("path", { d: "m159.78,40.67h12.03v7.34c3.09-4.38,9.75-8.39,17.59-8.39,15,0,26.35,12.78,26.35,28.45s-11.36,28.45-26.35,28.45c-7.9,0-14.57-4.07-17.59-8.46v30.18h-12.03V40.67Zm27.46,9.87c-9.75,0-16.42,7.65-16.42,17.53s6.67,17.53,16.42,17.53,16.29-7.65,16.29-17.53-6.67-17.53-16.29-17.53" }),
        /* @__PURE__ */ jsx22("rect", { x: "228.04", y: "13.33", width: "12.03", height: "82.02" }),
        /* @__PURE__ */ jsx22("path", { d: "m262.3,28.76c-4.38,0-8.08-3.58-8.08-8.08s3.7-7.96,8.08-7.96,7.96,3.64,7.96,7.96c0,4.51-3.46,8.08-7.96,8.08m-5.99,11.91h12.03v54.68h-12.03v-54.68Z" }),
        /* @__PURE__ */ jsx22("path", { d: "m309.45,96.46c-16.42,0-28.88-12.28-28.88-28.45s12.47-28.45,28.88-28.45c11.29,0,20.8,6.05,25.49,15.12l-10.49,5.68c-2.65-5.55-7.96-9.63-15-9.63-9.75,0-16.6,7.53-16.6,17.28s6.91,17.28,16.6,17.28c6.97,0,12.34-4.07,15-9.63l10.49,5.68c-4.69,9.13-14.26,15.12-25.49,15.12" }),
        /* @__PURE__ */ jsx22("path", { d: "m368.39,39.62c7.9,0,14.44,4.07,17.53,8.39v-7.34h12.03v54.68h-12.03v-7.34c-3.09,4.38-9.63,8.46-17.53,8.46-15,0-26.35-12.78-26.35-28.45s11.36-28.39,26.35-28.39m2.22,10.92c-9.75,0-16.29,7.65-16.29,17.53s6.54,17.53,16.29,17.53,16.29-7.65,16.29-17.53-6.6-17.53-16.29-17.53" }),
        /* @__PURE__ */ jsx22("polygon", { points: "418.56 95.41 418.56 51.41 407.14 51.41 407.14 40.67 418.56 40.67 418.56 25.49 430.6 25.49 430.6 40.67 451.33 40.67 451.33 51.41 430.6 51.41 430.6 84.61 451.33 84.61 451.33 95.41 418.56 95.41" }),
        /* @__PURE__ */ jsx22("path", { d: "m485.65,39.62c14.32,0,26.35,9.5,26.35,27.16,0,1.17,0,2.16-.12,3.95h-43.02c.56,8.95,7.78,14.87,16.85,14.87,7.65,0,12.78-3.52,15.61-8.33l9.2,6.54c-4.94,7.78-13.45,12.71-24.93,12.71-16.6,0-28.57-11.79-28.57-28.45.12-16.11,12.03-28.45,28.64-28.45m-16.17,22.28h30.43c-1.3-7.9-7.65-12.16-14.69-12.16s-14.07,4.07-15.74,12.16" })
      ]
    }
  );
});
function downloadSvg(filename, svg) {
  const link = document.createElement("a");
  link.href = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  link.download = filename;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function Logo() {
  return /* @__PURE__ */ jsx22("a", { className: "v2-header-logo", href: "/", children: /* @__PURE__ */ jsx22(GlyphSVG, {}) });
}
function MarketingLogo() {
  const [anchorRect, setAnchorRect] = useState3(null);
  const menu = Ariakit.useMenuStore({ focusLoop: true });
  const glyphRef = useRef3(null);
  const wordmarkRef = useRef3(null);
  const handleDownloadGlyph = () => {
    if (glyphRef.current) {
      const svgString = glyphRef.current.outerHTML;
      downloadSvg("replicate-glyph.svg", svgString);
    }
  };
  const handleDownloadWordmark = () => {
    if (wordmarkRef.current) {
      const svgString = wordmarkRef.current.outerHTML;
      downloadSvg("replicate-wordmark.svg", svgString);
    }
  };
  return /* @__PURE__ */ jsxs8(Fragment, { children: [
    /* @__PURE__ */ jsxs8(
      "a",
      {
        onContextMenu: (event) => {
          event.preventDefault();
          const bbox = event.currentTarget.getBoundingClientRect();
          setAnchorRect({ x: bbox.x, y: bbox.y + bbox.height });
          menu.show();
        },
        href: "/",
        className: "v2-marketing-logo",
        children: [
          /* @__PURE__ */ jsx22("div", { className: "v2-marketing-logo-glyph", children: /* @__PURE__ */ jsx22(GlyphSVG, { ref: glyphRef }) }),
          /* @__PURE__ */ jsx22("div", { className: "v2-marketing-logo-wordmark", children: /* @__PURE__ */ jsx22(WordmarkSVG, { ref: wordmarkRef }) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs8(
      Ariakit.Menu,
      {
        store: menu,
        modal: true,
        getAnchorRect: () => anchorRect,
        className: "v2-marketing-logo-menu",
        children: [
          /* @__PURE__ */ jsxs8(
            Ariakit.MenuItem,
            {
              autoFocus: true,
              onClick: handleDownloadGlyph,
              className: "v2-marketing-logo-menu-item",
              children: [
                /* @__PURE__ */ jsx22("span", { className: "v2-marketing-logo-menu-item-icon", children: /* @__PURE__ */ jsx22(GlyphSVG, {}) }),
                /* @__PURE__ */ jsx22("span", { children: "Download logo glyph as SVG" })
              ]
            }
          ),
          /* @__PURE__ */ jsxs8(
            Ariakit.MenuItem,
            {
              onClick: handleDownloadWordmark,
              className: "v2-marketing-logo-menu-item",
              children: [
                /* @__PURE__ */ jsx22("span", { className: "v2-marketing-logo-menu-item-icon", children: /* @__PURE__ */ jsx22(TextAa, { size: 20 }) }),
                /* @__PURE__ */ jsx22("span", { children: "Download wordmark as SVG" })
              ]
            }
          ),
          /* @__PURE__ */ jsxs8(
            Ariakit.MenuItem,
            {
              className: "v2-marketing-logo-menu-item",
              render: /* @__PURE__ */ jsx22("a", { href: "https://replicate.com/andreasjansson/plasma" }),
              children: [
                /* @__PURE__ */ jsx22("div", { className: "v2-marketing-logo-menu-item-icon v2-marketing-logo-menu-item-icon-gradient", children: /* @__PURE__ */ jsx22("div", { className: "v2-marketing-logo-menu-item-icon-gradient-inner" }) }),
                /* @__PURE__ */ jsx22("span", { children: "Generate your own goo" })
              ]
            }
          )
        ]
      }
    )
  ] });
}

// src/components/site-footer/index.tsx
import { jsx as jsx23, jsxs as jsxs9 } from "react/jsx-runtime";
var productLinks = [
  { label: "Explore", href: "https://replicate.com/explore" },
  {
    label: "Playground",
    href: "/playground",
    beta: true
  },
  { label: "Pricing", href: "https://replicate.com/pricing" },
  { label: "Docs", href: "/docs" },
  { label: "Blog", href: "/blog" },
  {
    label: "Changelog",
    href: "/changelog"
  }
];
var communityLinks = [
  { label: "Discord", href: "https://discord.gg/replicate" },
  { label: "X", href: "https://x.com/replicate" },
  { label: "GitHub", href: "https://github.com/replicate" }
];
var companyLinks = [
  { label: "Home", href: "https://replicate.com/home" },
  { label: "About", href: "/about" },
  { label: "Changelog", href: "https://replicate.com/changelog" },
  {
    label: "Join us",
    href: "/about#join-us"
  },
  { label: "Terms", href: "https://replicate.com/terms" },
  { label: "Privacy", href: "https://replicate.com/privacy" },
  { label: "Status", href: "https://replicatestatus.com/" },
  { label: "Support", href: "https://replicate.com/support" }
];
function SiteFooter({
  showBrandFlourish,
  showExpandedFooter,
  platformStatus,
  darkModeToggle
}) {
  return /* @__PURE__ */ jsxs9(
    "footer",
    {
      className: `site-footer ${showBrandFlourish ? "" : "site-footer--with-border"}`,
      children: [
        showExpandedFooter && showBrandFlourish ? /* @__PURE__ */ jsxs9("div", { "aria-hidden": "true", className: "site-footer-goo-container", children: [
          /* @__PURE__ */ jsx23(Goo, { speed: 0.2, resolution: 2, depth: 5 }),
          /* @__PURE__ */ jsxs9("div", { className: "site-footer-goo-overlay", children: [
            /* @__PURE__ */ jsx23("div", { className: "site-footer-goo-stripe" }),
            /* @__PURE__ */ jsx23("div", { className: "site-footer-goo-stripe" }),
            /* @__PURE__ */ jsx23("div", { className: "site-footer-goo-stripe" })
          ] })
        ] }) : null,
        showExpandedFooter ? /* @__PURE__ */ jsx23("div", { className: "site-footer-expanded", children: /* @__PURE__ */ jsxs9("div", { className: "site-footer-grid", children: [
          /* @__PURE__ */ jsx23("div", { className: "site-footer-logo-section", children: /* @__PURE__ */ jsx23("div", { className: "site-footer-logo", children: /* @__PURE__ */ jsx23(MarketingLogo, {}) }) }),
          /* @__PURE__ */ jsxs9("div", { className: "site-footer-product-section", children: [
            /* @__PURE__ */ jsx23("p", { className: "site-footer-section-title", children: "Product" }),
            /* @__PURE__ */ jsx23("ul", { className: "site-footer-link-list", children: productLinks.map((link) => {
              return /* @__PURE__ */ jsx23("li", { children: /* @__PURE__ */ jsxs9("a", { className: "site-footer-link group", href: link.href, children: [
                /* @__PURE__ */ jsx23("span", { className: "site-footer-link-text", children: link.label }),
                link.beta ? /* @__PURE__ */ jsxs9("span", { className: "site-footer-beta-badge", children: [
                  " ",
                  "(beta)"
                ] }) : null
              ] }) }, link.label);
            }) })
          ] }),
          /* @__PURE__ */ jsxs9("div", { children: [
            /* @__PURE__ */ jsx23("p", { className: "site-footer-section-title", children: "Community" }),
            /* @__PURE__ */ jsx23("ul", { className: "site-footer-link-list", children: communityLinks.map((link) => {
              return /* @__PURE__ */ jsx23("li", { children: /* @__PURE__ */ jsx23("a", { className: "site-footer-link--simple", href: link.href, children: link.label }) }, link.label);
            }) })
          ] }),
          /* @__PURE__ */ jsxs9("div", { children: [
            /* @__PURE__ */ jsx23("p", { className: "site-footer-section-title", children: "Company" }),
            /* @__PURE__ */ jsx23("ul", { className: "site-footer-link-list", children: companyLinks.map((link) => {
              return /* @__PURE__ */ jsx23("li", { children: /* @__PURE__ */ jsx23("a", { className: "site-footer-link--simple", href: link.href, children: link.label }) }, link.label);
            }) })
          ] })
        ] }) }) : null,
        /* @__PURE__ */ jsx23("div", { className: "site-footer-bottom", children: /* @__PURE__ */ jsxs9("div", { className: "site-footer-bottom-container", children: [
          /* @__PURE__ */ jsx23("div", { className: "site-footer-platform-status", children: platformStatus }),
          /* @__PURE__ */ jsxs9(
            "div",
            {
              className: `site-footer-bottom-content ${!showExpandedFooter ? "site-footer-bottom-content--full-width" : ""}`,
              children: [
                !showExpandedFooter ? /* @__PURE__ */ jsx23("ul", { children: companyLinks.map((link) => {
                  return /* @__PURE__ */ jsx23("li", { className: "site-footer-bottom-link", children: /* @__PURE__ */ jsx23(
                    "a",
                    {
                      className: "site-footer-bottom-link-text",
                      href: link.href,
                      children: link.label
                    }
                  ) }, link.label);
                }) }) : null,
                /* @__PURE__ */ jsx23("div", { className: "site-footer-dark-mode-toggle", children: darkModeToggle })
              ]
            }
          )
        ] }) })
      ]
    }
  );
}

// src/components/site-navigation-dropdown/index.tsx
import * as Ariakit2 from "@ariakit/react";
import { CaretDown as CaretDown3 } from "@phosphor-icons/react/dist/ssr";
import { jsx as jsx24, jsxs as jsxs10 } from "react/jsx-runtime";
function SiteNavigationDropdown({
  currentPath,
  items = [],
  anchorProps = () => ({}),
  buttonVariant = "outlined",
  buttonSize = "md",
  triggerText = "Dashboard",
  iconOnly = false
}) {
  const menuStore = Ariakit2.useMenuStore();
  return /* @__PURE__ */ jsxs10(Ariakit2.MenuProvider, { timeout: 0, placement: "bottom", store: menuStore, children: [
    /* @__PURE__ */ jsx24(
      Ariakit2.MenuButton,
      {
        render: iconOnly ? /* @__PURE__ */ jsx24(IconButton, { variant: buttonVariant, size: buttonSize, children: /* @__PURE__ */ jsx24(CaretDown3, {}) }) : /* @__PURE__ */ jsx24(
          Button,
          {
            variant: buttonVariant,
            size: buttonSize,
            endIcon: /* @__PURE__ */ jsx24(CaretDown3, { weight: "bold" })
          }
        ),
        children: iconOnly ? null : triggerText
      }
    ),
    /* @__PURE__ */ jsx24(
      Ariakit2.Menu,
      {
        hideOnHoverOutside: true,
        focusable: false,
        unmountOnHide: true,
        className: "v2-dropdown-menu v2-dropdown-menu--dashboard",
        children: items.map((item) => /* @__PURE__ */ jsxs10(
          Ariakit2.MenuItem,
          {
            tabbable: true,
            focusOnHover: false,
            blurOnHoverEnd: true,
            className: "v2-dropdown-menu-item",
            render: /* @__PURE__ */ jsx24(
              "a",
              __spreadValues({
                "data-active": isItemActive(item, currentPath),
                href: item.href
              }, anchorProps(item))
            ),
            children: [
              /* @__PURE__ */ jsx24("span", { className: "v2-dropdown-menu-item--icon", children: item.icon }),
              /* @__PURE__ */ jsxs10("div", { children: [
                /* @__PURE__ */ jsx24("div", { className: "v2-dropdown-menu-item--label", children: item.label }),
                /* @__PURE__ */ jsx24("div", { className: "v2-dropdown-menu-item--description", children: item.description })
              ] })
            ]
          },
          item.label
        ))
      }
    )
  ] });
}

// src/components/search/index.tsx
import { ComboboxItem as ComboboxItem5 } from "@ariakit/react";
import {
  Cube,
  CurrencyDollar,
  GraduationCap as GraduationCap2,
  GridNine as GridNine2,
  Lifebuoy,
  Password,
  Plus as Plus2,
  RocketLaunch,
  Sparkle as Sparkle2
} from "@phosphor-icons/react";
import { useId, useMemo as useMemo3, useState as useState5 } from "react";

// src/components/search/item.tsx
import { forwardRef as forwardRef9 } from "react";
import { SealCheck, SquaresFour } from "@phosphor-icons/react/dist/ssr";
import { Fragment as Fragment2, jsx as jsx25, jsxs as jsxs11 } from "react/jsx-runtime";
var SearchPageResultItem = forwardRef9((_a, ref) => {
  var _b = _a, { children, href, icon } = _b, rest = __objRest(_b, ["children", "href", "icon"]);
  const IconEl = icon ? icon : () => null;
  return /* @__PURE__ */ jsx25("a", __spreadProps(__spreadValues({ ref, href, className: "r8-search-page-item group" }, rest), { children: /* @__PURE__ */ jsx25("div", { className: "r8-search-page-item-container", children: /* @__PURE__ */ jsxs11("div", { className: "r8-search-page-item-content", children: [
    /* @__PURE__ */ jsx25("div", { className: "r8-search-page-item-icon", children: /* @__PURE__ */ jsx25(IconEl, { size: 20 }) }),
    /* @__PURE__ */ jsx25("span", { className: "r8-search-page-item-text", children })
  ] }) }) }));
});
var CollectionSearchResultItem = forwardRef9((_a, ref) => {
  var _b = _a, { collection } = _b, rest = __objRest(_b, ["collection"]);
  const collectionRoute = `/collections/${collection.slug}`;
  return /* @__PURE__ */ jsx25(
    "a",
    __spreadProps(__spreadValues({
      ref,
      href: collectionRoute,
      className: "r8-search-collection-item group"
    }, rest), {
      children: /* @__PURE__ */ jsxs11("div", { className: "r8-search-collection-item-content", children: [
        /* @__PURE__ */ jsx25("div", { className: "r8-search-collection-item-icon", children: /* @__PURE__ */ jsx25(SquaresFour, { className: "text-r8-gray-11", size: 20, weight: "fill" }) }),
        /* @__PURE__ */ jsx25("span", { className: "r8-search-collection-item-text", children: collection.name })
      ] })
    })
  );
});
var ModelSearchResultItem = forwardRef9((_a, ref) => {
  var _b = _a, { model, condensed } = _b, rest = __objRest(_b, ["model", "condensed"]);
  return /* @__PURE__ */ jsxs11(
    "a",
    __spreadProps(__spreadValues({
      ref,
      href: `/${model.username}/${model.name}`,
      className: "r8-search-model-item group"
    }, rest), {
      children: [
        /* @__PURE__ */ jsx25("div", { className: "r8-search-model-cover", children: model.cover_image ? /* @__PURE__ */ jsx25("img", { src: model.cover_image, alt: `Cover for ${model.name}` }) : null }),
        /* @__PURE__ */ jsxs11("div", { className: "r8-search-model-item-container", children: [
          /* @__PURE__ */ jsxs11("div", { className: "r8-search-model-item-header", children: [
            /* @__PURE__ */ jsxs11(
              "div",
              {
                className: `r8-search-model-item-title ${condensed ? "r8-search-model-item-title--condensed" : "r8-search-model-item-title--normal"}`,
                children: [
                  /* @__PURE__ */ jsx25("span", { className: "r8-search-model-username", children: model.username }),
                  /* @__PURE__ */ jsx25("span", { className: "r8-search-model-separator", children: "\u2009/\u2009" }),
                  /* @__PURE__ */ jsx25("span", { className: "r8-search-model-name", children: model.name }),
                  model.is_official_model ? /* @__PURE__ */ jsxs11(Fragment2, { children: [
                    " ",
                    /* @__PURE__ */ jsx25(SealCheck, { weight: "fill", className: "r8-search-model-verified" })
                  ] }) : null
                ]
              }
            ),
            /* @__PURE__ */ jsxs11("span", { className: "r8-search-model-runs", children: [
              model.run_count,
              " runs"
            ] })
          ] }),
          model.description && /* @__PURE__ */ jsx25("p", { className: "r8-search-model-description", children: model.description })
        ] })
      ]
    })
  );
});

// src/components/search/hooks.ts
import { useDebounce, useRequest } from "ahooks";
import { useEffect as useEffect4, useMemo as useMemo2, useState as useState4 } from "react";
function fetchSearchResults(_0) {
  return __async(this, arguments, function* ({ query, params }) {
    const url = new URL(
      "https://replicate-search-prototype-production.replicate.workers.dev/query"
    );
    url.searchParams.set("q", query);
    url.searchParams.set("limit", String(params.limit));
    const res = yield fetch(url);
    if (!res.ok) {
      throw new Error(
        `Failed to fetch search prototype results: ${res.statusText}`
      );
    }
    return yield res.json();
  });
}
function useSearchApi({
  query,
  debounceMs = 300,
  params
}) {
  const debouncedSearchQuery = useDebounce(query || "", {
    wait: debounceMs
  });
  const { loading, error, data, mutate } = useRequest(
    () => fetchSearchResults({ query: debouncedSearchQuery, params }),
    {
      manual: false,
      ready: Boolean(debouncedSearchQuery),
      refreshDeps: [debouncedSearchQuery],
      refreshOnWindowFocus: false,
      staleTime: 0
    }
  );
  return {
    loading,
    data,
    error,
    clear: () => mutate(void 0)
  };
}
function useMediaMatch(query) {
  const [matches, setMatches] = useState4(false);
  const matchMedia = useMemo2(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.matchMedia(query);
  }, [query]);
  useEffect4(() => {
    if (!matchMedia) {
      return;
    }
    setMatches(matchMedia.matches);
    const listener = (event) => setMatches(event.matches);
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener("change", listener);
      return () => matchMedia.removeEventListener("change", listener);
    }
    matchMedia.addListener(listener);
    return () => matchMedia.removeListener(listener);
  }, [matchMedia]);
  return matches;
}

// src/components/search/combobox.tsx
import * as Ariakit3 from "@ariakit/react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  KeyReturn,
  MagnifyingGlass
} from "@phosphor-icons/react";
import { useKeyPress } from "ahooks";
import clsx3 from "clsx";
import * as React from "react";
import { isMacOs } from "react-device-detect";
import { jsx as jsx26, jsxs as jsxs12 } from "react/jsx-runtime";
function ComboboxProvider3(_a) {
  var _b = _a, {
    tabId,
    setTabId,
    defaultTabId,
    onSearch,
    onTabChange
  } = _b, props = __objRest(_b, [
    "tabId",
    "setTabId",
    "defaultTabId",
    "onSearch",
    "onTabChange"
  ]);
  return /* @__PURE__ */ jsx26(
    Ariakit3.ComboboxProvider,
    __spreadProps(__spreadValues({}, props), {
      setValue: (value) => {
        var _a2;
        (_a2 = props.setValue) == null ? void 0 : _a2.call(props, value);
        React.startTransition(() => {
          onSearch == null ? void 0 : onSearch(value);
        });
      },
      children: /* @__PURE__ */ jsx26(
        TabProvider,
        {
          selectedId: tabId,
          defaultSelectedId: defaultTabId,
          setSelectedId: (id) => {
            if (!id) return;
            setTabId == null ? void 0 : setTabId(id);
            React.startTransition(() => {
              onTabChange == null ? void 0 : onTabChange(id);
            });
          },
          children: props.children
        }
      )
    })
  );
}
var Combobox3 = React.forwardRef(function Combobox4(_a, ref) {
  var _b = _a, { loading } = _b, rest = __objRest(_b, ["loading"]);
  const combobox = Ariakit3.useComboboxContext();
  const isOpen = Ariakit3.useStoreState(combobox, "open");
  useKeyPress(["meta.k"], (e) => {
    e.preventDefault();
    if (!combobox) return;
    combobox.toggle();
    combobox.move(null);
  });
  return /* @__PURE__ */ jsxs12("div", { className: "combobox-wrapper r8-search-combobox-wrapper", children: [
    /* @__PURE__ */ jsx26("div", { className: "r8-search-combobox-icon-left", children: /* @__PURE__ */ jsx26(MagnifyingGlass, { size: 15 }) }),
    /* @__PURE__ */ jsx26(
      Ariakit3.Combobox,
      __spreadProps(__spreadValues({
        ref,
        autoSelect: true,
        autoFocus: false,
        autoComplete: "none",
        autoCorrect: "off",
        autoCapitalize: "off",
        spellCheck: "false",
        maxLength: 100
      }, rest), {
        className: clsx3("combobox", rest.className)
      })
    ),
    /* @__PURE__ */ jsxs12("div", { className: "r8-search-combobox-icon-right", children: [
      loading && isOpen ? /* @__PURE__ */ jsx26("div", { className: "r8-search-combobox-spinner", children: /* @__PURE__ */ jsx26(Spinner, {}) }) : null,
      !isOpen ? /* @__PURE__ */ jsx26("div", { className: "r8-search-combobox-kbd-hint", children: /* @__PURE__ */ jsxs12("kbd", { className: "r8-search-combobox-kbd", children: [
        isMacOs ? "cmd" : "ctrl",
        "+k"
      ] }) }) : null
    ] })
  ] });
});
var ComboboxPopover3 = React.forwardRef(function ComboboxPopover4(props, ref) {
  const isDesktop = useMediaMatch("(min-width: 1024px)");
  const combobox = Ariakit3.useComboboxContext();
  const isInputActive = Ariakit3.useStoreState(
    combobox,
    (state) => (state == null ? void 0 : state.activeId) === null
  );
  const mounted = React.useDeferredValue(
    Ariakit3.useStoreState(combobox, "mounted")
  );
  return /* @__PURE__ */ jsxs12(
    Ariakit3.ComboboxPopover,
    __spreadProps(__spreadValues({
      ref,
      gutter: 8,
      unmountOnHide: true,
      fitViewport: true,
      sameWidth: isDesktop,
      autoFocusOnHide: false,
      className: "r8-search-combobox-popover",
      wrapperProps: {
        style: {
          width: "100%"
        }
      },
      hidden: !mounted
    }, props), {
      children: [
        mounted && props.children,
        /* @__PURE__ */ jsxs12("div", { className: "r8-search-combobox-footer", children: [
          /* @__PURE__ */ jsxs12("div", { className: "r8-search-combobox-shortcut", children: [
            /* @__PURE__ */ jsx26("kbd", { className: "kbd", "aria-label": "Up Arrow", children: /* @__PURE__ */ jsx26(ArrowUp, {}) }),
            /* @__PURE__ */ jsx26("kbd", { className: "kbd", "aria-label": "Down Arrow", children: /* @__PURE__ */ jsx26(ArrowDown, {}) }),
            /* @__PURE__ */ jsx26("span", { className: "r8-search-combobox-shortcut-text", children: "to navigate" })
          ] }),
          !isInputActive && /* @__PURE__ */ jsxs12("div", { className: "r8-search-combobox-shortcut", children: [
            /* @__PURE__ */ jsx26("kbd", { className: "kbd", "aria-label": "Left Arrow", children: /* @__PURE__ */ jsx26(ArrowLeft, {}) }),
            /* @__PURE__ */ jsx26("kbd", { className: "kbd", "aria-label": "Right Arrow", children: /* @__PURE__ */ jsx26(ArrowRight, {}) }),
            /* @__PURE__ */ jsx26("span", { className: "r8-search-combobox-shortcut-text", children: "to switch tabs" })
          ] }),
          !isInputActive && /* @__PURE__ */ jsxs12("div", { className: "r8-search-combobox-shortcut", children: [
            /* @__PURE__ */ jsx26("kbd", { className: "kbd", "aria-label": "Enter", children: /* @__PURE__ */ jsx26(KeyReturn, {}) }),
            /* @__PURE__ */ jsx26("span", { className: "r8-search-combobox-shortcut-text", children: "to select" })
          ] })
        ] })
      ]
    })
  );
});
var ComboboxTabList = React.forwardRef(function ComboboxTabList2(props, ref) {
  return /* @__PURE__ */ jsx26(
    TabList,
    __spreadProps(__spreadValues({
      ref
    }, props), {
      size: "sm",
      className: clsx3("r8-search-combobox-tab-list", props.className)
    })
  );
});
var ComboboxTab = React.forwardRef(function ComboboxTab2(props, ref) {
  return /* @__PURE__ */ jsx26(
    Tab,
    __spreadProps(__spreadValues({
      ref,
      accessibleWhenDisabled: false
    }, props), {
      className: clsx3("tab", props.className)
    })
  );
});
var ComboboxPanel = React.forwardRef(function ComboboxTabPanel(props, ref) {
  const tab = Ariakit3.useTabContext();
  const tabId = Ariakit3.useStoreState(
    tab,
    (state) => {
      var _a;
      return (_a = props.tabId) != null ? _a : state == null ? void 0 : state.selectedId;
    }
  );
  return /* @__PURE__ */ jsx26(
    TabPanel,
    __spreadProps(__spreadValues({
      ref,
      tabId
    }, props), {
      className: clsx3("tab-panel", props.className),
      children: /* @__PURE__ */ jsx26(Ariakit3.ComboboxList, { className: "listbox", children: props.children })
    })
  );
});
var ComboboxItem3 = React.forwardRef(
  function ComboboxItem4(props, ref) {
    return /* @__PURE__ */ jsx26(
      Ariakit3.ComboboxItem,
      __spreadProps(__spreadValues({
        ref,
        focusOnHover: true,
        blurOnHoverEnd: false
      }, props), {
        className: clsx3("combobox-item", props.className)
      })
    );
  }
);

// src/components/search/index.tsx
import {
  Article,
  BookOpenText,
  ListMagnifyingGlass,
  NewspaperClipping
} from "@phosphor-icons/react/dist/ssr";
import { jsx as jsx27, jsxs as jsxs13 } from "react/jsx-runtime";
function getTabId(category, prefix) {
  return `${prefix}/${category}`;
}
function getCategory(tabId, prefix) {
  return tabId.replace(`${prefix}/`, "");
}
function groupBy(array, key) {
  return array.reduce(
    (acc, item) => {
      const groupKey = String(item[key]);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {}
  );
}
var unAuthedPages = {
  "/playground": {
    name: "Playground (beta)",
    href: "/playground",
    icon: GridNine2
  },
  "/docs": {
    name: "Docs",
    href: "/docs",
    icon: BookOpenText
  },
  "/blog": {
    name: "Blog",
    href: "/blog",
    icon: Article
  },
  "/changelog": {
    name: "Changelog",
    href: "/changelog",
    icon: NewspaperClipping
  }
};
var authedPages = {
  "/models": {
    name: "Models",
    href: "/models",
    icon: Cube
  },
  "/predictions": {
    name: "Predictions",
    href: "/predictions",
    icon: Sparkle2
  },
  "/trainings": {
    name: "Trainings",
    href: "/trainings",
    icon: GraduationCap2
  },
  "/deployments": {
    name: "Deployments",
    href: "/deployments",
    icon: RocketLaunch
  },
  "/account/billing": {
    name: "Billing",
    href: "/account/billing",
    icon: CurrencyDollar
  },
  "/support": {
    name: "Support",
    href: "/support",
    icon: Lifebuoy
  },
  "/account/api-tokens": {
    name: "API Tokens",
    href: "/account/api-tokens",
    icon: Password
  },
  "/create": {
    name: "Create model",
    href: "/create",
    icon: Plus2
  }
};
var categories = ["All", "Models", "Collections", "Pages"];
function Search({
  authState,
  params = {
    limit: 50
  }
}) {
  const prefix = useId();
  const [searchValue, setSearchValue] = useState5("");
  const [tabId, setTabId] = useState5(getTabId("All", prefix));
  const searchResults = useSearchApi({
    query: searchValue,
    params
  });
  const isLoading = searchResults.loading;
  const modelResults = useMemo3(() => {
    if (!searchResults.data) {
      return [];
    }
    return searchResults.data.results.map((result) => {
      const [username, name] = result.name.split("/");
      return {
        username,
        name,
        description: result.description,
        run_count: result.run_count.toLocaleString(),
        owner_avatar: { kind: "organization", url: result.avatar_url },
        cover_image: result.cover_image_url,
        latest_enabled_version_id: null,
        hardware: "",
        is_official_model: result.is_official,
        nwo: result.name
      };
    });
  }, [searchResults]);
  const collectionResults = useMemo3(() => {
    if (!searchResults.data) {
      return [];
    }
    return searchResults.data.collections;
  }, [searchResults]);
  const pageResults = useMemo3(() => {
    const mapApiPageToDisplayPage = (apiPage) => {
      const pageConfig = authState === "authenticated" ? __spreadValues(__spreadValues({}, unAuthedPages), authedPages)[apiPage.href] : unAuthedPages[apiPage.href];
      return {
        name: (pageConfig == null ? void 0 : pageConfig.name) || apiPage.name,
        href: apiPage.href,
        icon: (pageConfig == null ? void 0 : pageConfig.icon) || GridNine2
      };
    };
    if (!searchResults.data) {
      return authState === "authenticated" ? Object.values(authedPages) : Object.values(unAuthedPages);
    }
    const unauthedPageResults = searchResults.data.pages.filter((page) => Object.keys(unAuthedPages).includes(page.href)).map(mapApiPageToDisplayPage);
    const authedPageResults = searchResults.data.pages.filter((page) => Object.keys(authedPages).includes(page.href)).map(mapApiPageToDisplayPage);
    const pages = authState === "authenticated" ? [...unauthedPageResults, ...authedPageResults] : [...unauthedPageResults];
    return pages;
  }, [searchResults, authState]);
  const matches = useMemo3(() => {
    const modelItems = modelResults.map((item) => __spreadProps(__spreadValues({}, item), {
      category: "Models",
      type: "model"
    }));
    const collectionItems = collectionResults.map(
      (item) => __spreadProps(__spreadValues({}, item), {
        category: "Collections",
        type: "collection"
      })
    );
    const pageItems = pageResults.map((item) => __spreadProps(__spreadValues({}, item), {
      category: "Pages",
      type: "page"
    }));
    const allResults = [
      ...modelItems,
      ...collectionItems,
      ...pageItems
    ];
    const groups = groupBy(allResults, "category");
    groups.All = allResults;
    groups.Models = groups.Models || [];
    groups.Collections = groups.Collections || [];
    groups.Pages = groups.Pages || [];
    return groups;
  }, [modelResults, collectionResults, pageResults]);
  const currentPages = matches[getCategory(tabId, prefix)] || [];
  return /* @__PURE__ */ jsxs13(
    ComboboxProvider3,
    {
      defaultTabId: tabId,
      onTabChange: setTabId,
      onSearch: setSearchValue,
      resetValueOnHide: true,
      setOpen: (open) => {
        if (!open) {
          searchResults.clear();
        }
      },
      children: [
        /* @__PURE__ */ jsx27(
          Combobox3,
          {
            className: "r8-search-combobox",
            placeholder: "Search 'best image models' or 'text to image'",
            loading: isLoading && Boolean(searchValue)
          }
        ),
        /* @__PURE__ */ jsxs13(ComboboxPopover3, { "aria-label": "Pages", children: [
          /* @__PURE__ */ jsx27(ComboboxTabList, { "aria-label": "Categories", children: categories.map((category) => {
            const currentPages2 = matches[category];
            return /* @__PURE__ */ jsx27(
              ComboboxTab,
              {
                id: getTabId(category, prefix),
                disabled: !(currentPages2 == null ? void 0 : currentPages2.length),
                children: /* @__PURE__ */ jsxs13("div", { className: "r8-search-tab-content", children: [
                  /* @__PURE__ */ jsx27("span", { children: category }),
                  /* @__PURE__ */ jsx27("div", { children: /* @__PURE__ */ jsx27(Badge, { rounded: true, size: "sm", children: (currentPages2 == null ? void 0 : currentPages2.length) || 0 }) })
                ] })
              },
              category
            );
          }) }),
          /* @__PURE__ */ jsxs13(ComboboxPanel, { children: [
            searchValue && isLoading ? /* @__PURE__ */ jsxs13("div", { className: "r8-search-loading", children: [
              /* @__PURE__ */ jsx27(GlyphLoader, {}),
              /* @__PURE__ */ jsx27("p", { className: "r8-search-loading-text", children: "Searching..." })
            ] }) : null,
            searchValue ? /* @__PURE__ */ jsx27(
              ComboboxItem5,
              {
                render: /* @__PURE__ */ jsxs13(
                  SearchPageResultItem,
                  {
                    icon: ListMagnifyingGlass,
                    href: `/search?query=${encodeURIComponent(searchValue)}`,
                    children: [
                      "View all results for",
                      " ",
                      /* @__PURE__ */ jsx27("span", { className: "font-semibold", children: searchValue })
                    ]
                  }
                )
              },
              "search-page"
            ) : null,
            currentPages.map((item, i) => {
              switch (item.type) {
                case "model":
                  return /* @__PURE__ */ jsx27(
                    ComboboxItem5,
                    {
                      render: /* @__PURE__ */ jsx27(ModelSearchResultItem, { condensed: true, model: item })
                    },
                    `${item.username}-${item.name}-${i}`
                  );
                case "collection":
                  return /* @__PURE__ */ jsx27(
                    ComboboxItem5,
                    {
                      render: /* @__PURE__ */ jsx27(CollectionSearchResultItem, { collection: item })
                    },
                    `${item.slug}-${i}`
                  );
                case "page":
                  return /* @__PURE__ */ jsx27(
                    ComboboxItem5,
                    {
                      render: /* @__PURE__ */ jsx27(SearchPageResultItem, { icon: item.icon, href: item.href, children: item.name })
                    },
                    `${item.href}-${i}`
                  );
                default:
                  return null;
              }
            })
          ] })
        ] })
      ]
    }
  );
}

// src/components/platform-status-badge/index.tsx
import { jsx as jsx28, jsxs as jsxs14 } from "react/jsx-runtime";
function PlatformStatusBadge({
  status,
  url = "https://statuspage.incident.io/replicate"
}) {
  return /* @__PURE__ */ jsx28("div", { children: /* @__PURE__ */ jsxs14(
    "a",
    {
      href: url,
      className: "r8-platform-status-badge",
      "data-status": status,
      target: "_blank",
      rel: "noreferrer",
      children: [
        /* @__PURE__ */ jsx28("div", { className: "r8-platform-status-badge--indicator" }),
        /* @__PURE__ */ jsx28("div", { className: "r8-platform-status-badge--label", children: /* @__PURE__ */ jsx28("span", { children: status === "healthy" ? "All services are online" : "Some services may be impacted" }) })
      ]
    }
  ) });
}

// src/cover-image.ts
import mime from "mime";
function parseCoverImageUrl({
  url
}) {
  if (!url) return null;
  const mimeType = mime.getType(url);
  if (!mimeType) return null;
  if (mimeType.startsWith("video/")) {
    return { fileType: "video", url };
  }
  if (mimeType.startsWith("image/")) {
    return { fileType: "image", url };
  }
  return null;
}

// src/index.ts
import { VisuallyHidden, Group, GroupLabel, Command } from "@ariakit/react";
export {
  Avatar,
  Badge,
  Banner,
  Box,
  Button,
  Checkbox,
  CheckboxProvider,
  Combobox,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxPopover,
  ComboboxProvider,
  Command,
  Dialog,
  DialogDescription,
  DialogDisclosure,
  DialogDismiss,
  DialogHeading,
  DialogProvider,
  Disclosure,
  DisclosureContent,
  DisclosureProvider,
  FormGroup,
  GlyphLoader,
  Goo,
  Group,
  GroupLabel,
  IconButton,
  Label,
  Logo,
  MarketingLogo,
  Menu,
  MenuButton,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuItemCheck,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuProvider,
  MenuSeparator,
  NativeSelect,
  PlatformStatusBadge,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverDescription,
  PopoverDisclosure,
  PopoverDismiss,
  PopoverHeading,
  PopoverProvider,
  Radio,
  RadioGroup,
  RadioProvider,
  Search,
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectItemCheck,
  SelectLabel,
  SelectList,
  SelectPopover,
  SelectProvider,
  SelectRow,
  SiteFooter,
  SiteNavigation,
  SiteNavigationDropdown,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabProvider,
  TextField,
  Textarea,
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
  VisuallyHidden,
  badge,
  banner,
  box,
  dashboardMenu,
  dialog,
  exploreMenu,
  formGroup,
  parseCoverImageUrl,
  selectItem,
  tabs,
  useCheckboxContext,
  useCheckboxStore,
  useComboboxContext,
  useComboboxStore,
  useDialogContext,
  useDialogStore,
  useDisclosureContext,
  useDisclosureStore,
  useMenuContext,
  useMenuStore,
  usePopoverContext,
  usePopoverStore,
  useRadioContext,
  useRadioStore,
  useSelectContext,
  useSelectStore,
  useTabContext,
  useTabStore,
  useTooltipStore
};
//# sourceMappingURL=index.mjs.map